<template>
  <v-container class="admin-container">
  <div class="admin-view">
    <!-- Hintergrundelemente für Glasmorphismus-Effekt -->
    <div class="background-elements">
      <div class="ambient-light ambient-light-1"></div>
      <div class="ambient-light ambient-light-2"></div>
      <div class="ambient-light ambient-light-3"></div>
      <div class="grid-overlay"></div>
    </div>

    <!-- Moderne Tab-Navigation mit Glaseffekt -->
    <div class="admin-tabs-container">
      <div class="admin-tabs-wrapper">
        <div
          v-for="tab in tabItems"
          :key="tab.value"
          class="admin-tab"
          :class="{ active: activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          <div class="tab-content">
            <v-icon
              :color="activeTab === tab.value ? 'white' : 'success'"
              size="20"
              >{{ tab.icon }}</v-icon
            >
            <span class="tab-title">{{ tab.title }}</span>
          </div>
          <div class="tab-indicator" v-if="activeTab === tab.value"></div>
        </div>
      </div>
    </div>

    <!-- Haupt-Content-Bereich -->
    <v-card class="admin-card">
      <!-- Transition für Tab-Inhalte -->
      <transition name="fade-transition" mode="out-in">
        <!-- 👥 Nutzerverwaltung -->
        <div v-if="activeTab === 'users'" class="tab-content-container">
          <div class="section-header">
            <div class="header-title">
              <v-icon color="success" size="28" class="mr-3"
                >mdi-account-group</v-icon
              >
              <h1>Nutzerverwaltung</h1>
            </div>
            <div class="header-actions">
              <v-btn
                color="success"
                variant="elevated"
                class="action-btn"
                @click="openRegisterDialog"
              >
                <v-icon start>mdi-account-plus</v-icon>
                Nutzer hinzufügen
              </v-btn>
              <v-btn
                color="success"
                variant="text"
                class="action-btn"
                @click="cleanupExpiredTokens"
                :loading="cleaningTokens"
              >
                <v-icon start>mdi-database-refresh</v-icon>
                Abgelaufene Tokens löschen
              </v-btn>
              <v-btn
                icon
                variant="text"
                color="success"
                class="refresh-btn"
                @click="fetchUsers"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>

          <v-divider class="admin-divider"></v-divider>

          <!-- Filterleiste -->
          <div class="filter-bar">
            <v-text-field
              v-model="userSearch"
              prepend-inner-icon="mdi-magnify"
              label="Nutzer suchen"
              variant="outlined"
              density="comfortable"
              hide-details
              bg-color="rgba(30, 30, 40, 0.5)"
              class="search-field"
              clearable
            ></v-text-field>

            <div class="filter-options">
              <v-chip-group
                v-model="selectedUserFilters"
                multiple
                selected-class="selected-filter"
              >
                <v-chip filter value="developer" class="filter-chip">
                  <v-icon start size="small">mdi-shield-account</v-icon>
                  Developer
                </v-chip>
                <v-chip filter value="super_admin" class="filter-chip">
                  <v-icon start size="small">mdi-shield-account</v-icon>
                  Super Admin
                </v-chip>
                <v-chip filter value="admin" class="filter-chip">
                  <v-icon start size="small">mdi-shield-account</v-icon>
                  Admin
                </v-chip>
                <v-chip filter value="active" class="filter-chip">
                  <v-icon start size="small">mdi-check-circle</v-icon>
                  Aktiv
                </v-chip>
                <v-chip filter value="lagerist" class="filter-chip">
                  <v-icon start size="small">mdi-warehouse</v-icon>
                  Lagerist
                </v-chip>
                <v-chip filter value="monteur" class="filter-chip">
                  <v-icon start size="small">mdi-hammer-screwdriver</v-icon>
                  Monteur
                </v-chip>
                <v-chip filter value="office" class="filter-chip">
                  <v-icon start size="small">mdi-briefcase</v-icon>
                  Office
                </v-chip>
                <v-chip filter value="refurbish" class="filter-chip">
                  <v-icon start size="small">mdi-tools</v-icon>
                  Refurbish
                </v-chip>
                <v-chip filter value="dtc" class="filter-chip">
                  <v-icon start size="small">mdi-package-variant-closed</v-icon>
                  DTC
                </v-chip>
                <v-chip filter value="inventur" class="filter-chip">
                  <v-icon start size="small">mdi-clipboard-list</v-icon>
                  Inventur
                </v-chip>
              </v-chip-group>
            </div>
          </div>

          <!-- Nutzerkarten-Grid -->
          <div class="users-grid">
            <div v-for="user in filteredUsers" :key="user.id" class="user-card-modern">
              <!-- Kompakter Header -->
              <div class="user-card-modern-header">
                <div class="user-header-left">
                  <div
                    class="user-avatar-modern"
                    :class="{
                      'avatar-admin': user.admin || user.super_admin,
                      'avatar-developer': user.developer,
                    }"
                  >
                    <v-icon
                      :color="
                        user.developer
                          ? 'yellow-darken-2'
                          : user.admin || user.super_admin
                          ? 'red-lighten-1'
                          : 'success'
                      "
                      size="28"
                    >
                      {{
                        user.developer
                          ? "mdi-code-braces"
                          : user.super_admin
                          ? "mdi-shield-crown"
                          : user.admin
                          ? "mdi-shield-account"
                          : "mdi-account-circle"
                      }}
                    </v-icon>
                  </div>
                  <div class="user-name-block">
                    <h3 class="user-name-modern">
                      {{ user.vorname }} {{ user.nachname }}
                    </h3>
                    <p class="user-login-modern">@{{ user.loginname }}</p>
                  </div>
                </div>

                <v-chip
                  :color="user.aktiv ? 'success' : 'error'"
                  size="small"
                  variant="flat"
                  class="status-chip"
                >
                  <v-icon start size="x-small">{{
                    user.aktiv ? "mdi-check-circle" : "mdi-close-circle"
                  }}</v-icon>
                  {{ user.aktiv ? "Aktiv" : "Inaktiv" }}
                </v-chip>
              </div>

              <!-- Info Grid -->
              <div class="user-info-grid">
                <div class="info-item">
                  <div class="info-label">
                    <v-icon size="14" color="grey-lighten-1">mdi-email</v-icon>
                    <span>E-Mail</span>
                  </div>
                  <div class="info-value">{{ user.email }}</div>
                </div>

                <div class="info-item">
                  <div class="info-label">
                    <v-icon size="14" color="grey-lighten-1">mdi-badge-account</v-icon>
                    <span>Mitarbeiter-Nr.</span>
                  </div>
                  <div class="info-value">{{ user.mitarbeiternummer }}</div>
                </div>

                <div class="info-item">
                  <div class="info-label">
                    <v-icon size="14" color="grey-lighten-1">mdi-office-building</v-icon>
                    <span>Abteilung</span>
                  </div>
                  <div class="info-value">{{ user.abteilung || 'Keine Abteilung' }}</div>
                </div>

                <div class="info-item">
                  <div class="info-label">
                    <v-icon size="14" color="grey-lighten-1">mdi-login-variant</v-icon>
                    <span>Letzter Login</span>
                  </div>
                  <div class="info-value info-value-small">
                    {{
                      user.last_login
                        ? formatDateTime(user.last_login)
                        : "Noch nie"
                    }}
                  </div>
                </div>
              </div>

              <!-- Rollen - Alle anzeigen, aktive hervorgehoben -->
              <div class="user-roles-modern">
                <div class="roles-label">Rollen:</div>
                <div class="roles-badges">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.developer ? 'yellow-darken-3' : 'grey-darken-2'"
                        size="small"
                        :variant="user.developer ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleDeveloperStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.developer ? 'mdi-code-braces' : 'mdi-code-braces-outline'
                        }}</v-icon>
                        Developer
                      </v-chip>
                    </template>
                    <span>{{ user.developer ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.super_admin ? 'deep-purple' : 'grey-darken-2'"
                        size="small"
                        :variant="user.super_admin ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleSuperAdminStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.super_admin ? 'mdi-shield-crown' : 'mdi-shield-crown-outline'
                        }}</v-icon>
                        Super Admin
                      </v-chip>
                    </template>
                    <span>{{ user.super_admin ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.admin ? 'red-darken-1' : 'grey-darken-2'"
                        size="small"
                        :variant="user.admin ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleAdminStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.admin ? 'mdi-shield-account' : 'mdi-shield-account-outline'
                        }}</v-icon>
                        Admin
                      </v-chip>
                    </template>
                    <span>{{ user.admin ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.lagerist ? 'blue-darken-1' : 'grey-darken-2'"
                        size="small"
                        :variant="user.lagerist ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleLageristStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.lagerist ? 'mdi-warehouse' : 'mdi-warehouse-outline'
                        }}</v-icon>
                        Lagerist
                      </v-chip>
                    </template>
                    <span>{{ user.lagerist ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.monteur ? 'cyan-darken-1' : 'grey-darken-2'"
                        size="small"
                        :variant="user.monteur ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleMonteurStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.monteur ? 'mdi-hammer-screwdriver' : 'mdi-hammer-screwdriver-outline'
                        }}</v-icon>
                        Monteur
                      </v-chip>
                    </template>
                    <span>{{ user.monteur ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.returen_sorting ? 'teal-darken-1' : 'grey-darken-2'"
                        size="small"
                        :variant="user.returen_sorting ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleReturenSortingStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.returen_sorting ? 'mdi-sort' : 'mdi-sort-outline'
                        }}</v-icon>
                        Retouren Sorting
                      </v-chip>
                    </template>
                    <span>{{ user.returen_sorting ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.returen_sorting_v2 ? 'green-darken-1' : 'grey-darken-2'"
                        size="small"
                        :variant="user.returen_sorting_v2 ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleReturenSortingV2Status(user)"
                      >
                        <v-icon start size="14">{{
                          user.returen_sorting_v2 ? 'mdi-package-variant-closed' : 'mdi-package-variant-closed-outline'
                        }}</v-icon>
                        Retouren Sorting V2
                      </v-chip>
                    </template>
                    <span>{{ user.returen_sorting_v2 ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.office ? 'indigo-darken-1' : 'grey-darken-2'"
                        size="small"
                        :variant="user.office ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleOfficeStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.office ? 'mdi-briefcase' : 'mdi-briefcase-outline'
                        }}</v-icon>
                        Office
                      </v-chip>
                    </template>
                    <span>{{ user.office ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.refurbish ? 'orange-darken-1' : 'grey-darken-2'"
                        size="small"
                        :variant="user.refurbish ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleRefurbishStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.refurbish ? 'mdi-tools' : 'mdi-tools-outline'
                        }}</v-icon>
                        Refurbish
                      </v-chip>
                    </template>
                    <span>{{ user.refurbish ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.dtc ? 'teal-darken-1' : 'grey-darken-2'"
                        size="small"
                        :variant="user.dtc ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleDtcStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.dtc ? 'mdi-package-variant-closed' : 'mdi-package-variant'
                        }}</v-icon>
                        DTC
                      </v-chip>
                    </template>
                    <span>{{ user.dtc ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        :color="user.inventur ? 'amber-darken-3' : 'grey-darken-2'"
                        size="small"
                        :variant="user.inventur ? 'flat' : 'outlined'"
                        class="role-chip"
                        @click="toggleInventurStatus(user)"
                      >
                        <v-icon start size="14">{{
                          user.inventur ? 'mdi-clipboard-list' : 'mdi-clipboard-list-outline'
                        }}</v-icon>
                        Inventur
                      </v-chip>
                    </template>
                    <span>{{ user.inventur ? 'Klicken zum Entfernen' : 'Klicken zum Hinzufügen' }}</span>
                  </v-tooltip>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="user-actions-modern">
                <div class="primary-actions">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        :color="user.aktiv ? 'warning' : 'success'"
                        variant="tonal"
                        size="small"
                        icon
                        @click="toggleUserStatus(user)"
                      >
                        <v-icon size="18">{{
                          user.aktiv ? "mdi-account-off" : "mdi-account-check"
                        }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ user.aktiv ? "Deaktivieren" : "Aktivieren" }}</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        color="primary"
                        variant="tonal"
                        size="small"
                        icon
                        @click="openEditDialog(user)"
                      >
                        <v-icon size="18">mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Bearbeiten</span>
                  </v-tooltip>

                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        color="error"
                        variant="tonal"
                        size="small"
                        icon
                        @click="confirmDeleteUser(user)"
                      >
                        <v-icon size="18">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Löschen</span>
                  </v-tooltip>
                </div>

                <v-menu location="bottom end">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      color="grey"
                      variant="tonal"
                      size="small"
                      icon
                    >
                      <v-icon size="18">mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item @click="showUserSessions(user)">
                      <template v-slot:prepend>
                        <v-icon size="small">mdi-login-variant</v-icon>
                      </template>
                      <v-list-item-title>Sessions anzeigen</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="sendPasswordResetLink(user)">
                      <template v-slot:prepend>
                        <v-icon size="small">mdi-lock-reset</v-icon>
                      </template>
                      <v-list-item-title>Passwort-Link senden</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
        </div>

        <!-- � Sammelaccounts -->
        <div
          v-else-if="activeTab === 'shared-accounts'"
          class="tab-content-container"
        >
          <SharedAccountsTab />
        </div>

        <!-- �🔒 Gesperrte Benutzer -->
        <div
          v-else-if="activeTab === 'locked'"
          class="tab-content-container"
        >
          <div class="section-header">
            <div class="header-title">
              <v-icon color="error" size="28" class="mr-3"
                >mdi-lock-alert</v-icon
              >
              <h1>Gesperrte Benutzer</h1>
            </div>
            <div class="header-actions">
              <v-btn
                icon
                variant="text"
                color="success"
                class="refresh-btn"
                @click="fetchLockedUsers"
                :loading="loadingLockedUsers"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>

          <v-divider class="admin-divider"></v-divider>

          <!-- Info-Banner -->
          <v-alert
            v-if="lockedUsers.length === 0 && !loadingLockedUsers"
            type="success"
            variant="tonal"
            class="ma-4"
          >
            <v-icon start>mdi-check-circle</v-icon>
            Aktuell sind keine Benutzer gesperrt.
          </v-alert>

          <!-- Gesperrte Benutzer Liste -->
          <div v-else class="locked-users-container">
            <v-card
              v-for="locked in lockedUsers"
              :key="locked.id"
              class="locked-user-card"
              elevation="2"
            >
              <v-card-text>
                <div class="locked-user-header">
                  <div class="locked-user-info">
                    <v-icon color="error" size="40" class="mr-4"
                      >mdi-account-lock</v-icon
                    >
                    <div>
                      <div class="locked-user-name">
                        {{ locked.vorname || locked.username }}
                        {{ locked.nachname || "" }}
                      </div>
                      <div class="locked-user-details">
                        <v-chip size="small" color="error" variant="tonal">
                          <v-icon start size="small">mdi-account</v-icon>
                          {{ locked.username }}
                        </v-chip>
                        <v-chip
                          size="small"
                          color="warning"
                          variant="tonal"
                          class="ml-2"
                        >
                          <v-icon start size="small">mdi-ip-network</v-icon>
                          {{ locked.ip_address }}
                        </v-chip>
                      </div>
                    </div>
                  </div>

                  <div class="locked-user-status">
                    <v-chip color="error" size="large" class="status-chip">
                      <v-icon start>mdi-clock-alert</v-icon>
                      {{ formatRemainingTime(locked.remaining_minutes) }}
                    </v-chip>
                  </div>
                </div>

                <v-divider class="my-4"></v-divider>

                <div class="locked-user-stats">
                  <div class="stat-item">
                    <v-icon color="error" size="20">mdi-alert-circle</v-icon>
                    <span class="stat-label">Fehlversuche:</span>
                    <span class="stat-value">{{ locked.failed_attempts }}</span>
                  </div>

                  <div class="stat-item" v-if="locked.email">
                    <v-icon color="info" size="20">mdi-email</v-icon>
                    <span class="stat-label">E-Mail:</span>
                    <span class="stat-value">{{ locked.email }}</span>
                  </div>

                  <div class="stat-item" v-if="locked.abteilung">
                    <v-icon color="success" size="20">mdi-briefcase</v-icon>
                    <span class="stat-label">Abteilung:</span>
                    <span class="stat-value">{{ locked.abteilung }}</span>
                  </div>

                  <div class="stat-item" v-if="locked.locked_until">
                    <v-icon color="warning" size="20">mdi-calendar-clock</v-icon>
                    <span class="stat-label">Gesperrt bis:</span>
                    <span class="stat-value">{{
                      new Date(locked.locked_until).toLocaleString("de-DE")
                    }}</span>
                  </div>
                </div>
              </v-card-text>

              <v-card-actions class="locked-user-actions">
                <v-btn
                  color="success"
                  variant="elevated"
                  @click="confirmUnlock(locked)"
                >
                  <v-icon start>mdi-lock-open</v-icon>
                  Entsperren
                </v-btn>

                <v-btn
                  color="info"
                  variant="outlined"
                  @click="showLoginHistory(locked)"
                >
                  <v-icon start>mdi-history</v-icon>
                  Verlauf anzeigen
                </v-btn>

                <v-btn
                  color="warning"
                  variant="text"
                  @click="clearLoginHistory(locked)"
                >
                  <v-icon start>mdi-delete-sweep</v-icon>
                  Verlauf löschen
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>
        </div>

        <!-- 🛡️ Login-Versuche -->
        <div
          v-else-if="activeTab === 'login-attempts'"
          class="tab-content-container"
        >
          <div class="section-header">
            <div class="header-title">
              <v-icon color="info" size="28" class="mr-3"
                >mdi-shield-account</v-icon
              >
              <h1>Login-Versuche</h1>
            </div>
            <div class="header-actions">
              <v-btn
                icon
                variant="text"
                color="success"
                class="refresh-btn"
                @click="fetchLoginAttempts"
                :loading="loadingLoginAttempts"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>

          <v-divider class="admin-divider"></v-divider>

          <!-- Filter & Statistiken -->
          <div class="login-attempts-header">
            <!-- Statistiken -->
            <div class="stats-grid">
              <v-card class="stat-card">
                <v-card-text>
                  <div class="stat-content">
                    <v-icon color="info" size="32">mdi-chart-line</v-icon>
                    <div class="stat-details">
                      <div class="stat-value">{{ loginAttemptsStats.total_attempts }}</div>
                      <div class="stat-label">Gesamt Versuche</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card class="stat-card">
                <v-card-text>
                  <div class="stat-content">
                    <v-icon color="success" size="32">mdi-check-circle</v-icon>
                    <div class="stat-details">
                      <div class="stat-value">{{ loginAttemptsStats.successful_attempts }}</div>
                      <div class="stat-label">Erfolgreich</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card class="stat-card">
                <v-card-text>
                  <div class="stat-content">
                    <v-icon color="error" size="32">mdi-close-circle</v-icon>
                    <div class="stat-details">
                      <div class="stat-value">{{ loginAttemptsStats.failed_attempts }}</div>
                      <div class="stat-label">Fehlgeschlagen</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card class="stat-card">
                <v-card-text>
                  <div class="stat-content">
                    <v-icon color="warning" size="32">mdi-account-multiple</v-icon>
                    <div class="stat-details">
                      <div class="stat-value">{{ loginAttemptsStats.unique_users }}</div>
                      <div class="stat-label">Unique Users</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card class="stat-card">
                <v-card-text>
                  <div class="stat-content">
                    <v-icon color="purple" size="32">mdi-ip-network</v-icon>
                    <div class="stat-details">
                      <div class="stat-value">{{ loginAttemptsStats.unique_ips }}</div>
                      <div class="stat-label">Unique IPs</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <!-- Filter -->
            <v-card class="filter-card mt-4">
              <v-card-text>
                <div class="filter-grid">
                  <v-text-field
                    v-model="loginAttemptsFilter.username"
                    label="Username"
                    variant="outlined"
                    density="compact"
                    clearable
                    prepend-inner-icon="mdi-account"
                    @update:model-value="fetchLoginAttempts"
                  ></v-text-field>

                  <v-text-field
                    v-model="loginAttemptsFilter.ip_address"
                    label="IP-Adresse"
                    variant="outlined"
                    density="compact"
                    clearable
                    prepend-inner-icon="mdi-ip-network"
                    @update:model-value="fetchLoginAttempts"
                  ></v-text-field>

                  <v-select
                    v-model="loginAttemptsFilter.success"
                    :items="[
                      { title: 'Alle', value: null },
                      { title: 'Erfolgreich', value: 1 },
                      { title: 'Fehlgeschlagen', value: 0 }
                    ]"
                    label="Status"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-filter"
                    @update:model-value="fetchLoginAttempts"
                  ></v-select>

                  <v-select
                    v-model="loginAttemptsFilter.days"
                    :items="[
                      { title: 'Letzte 7 Tage', value: 7 },
                      { title: 'Letzte 30 Tage', value: 30 },
                      { title: 'Letzte 90 Tage', value: 90 },
                      { title: 'Letztes Jahr', value: 365 }
                    ]"
                    label="Zeitraum"
                    variant="outlined"
                    density="compact"
                    prepend-inner-icon="mdi-calendar-range"
                    @update:model-value="fetchLoginAttempts"
                  ></v-select>

                  <v-btn
                    color="warning"
                    variant="outlined"
                    @click="clearLoginAttemptsFilter"
                    class="mt-1"
                  >
                    <v-icon start>mdi-filter-remove</v-icon>
                    Filter zurücksetzen
                  </v-btn>

                  <v-btn
                    color="error"
                    variant="outlined"
                    @click="confirmDeleteOldAttempts"
                    class="mt-1"
                  >
                    <v-icon start>mdi-delete-sweep</v-icon>
                    Alte Einträge löschen
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <!-- Login-Versuche Tabelle -->
          <v-card class="mt-4">
            <v-data-table
              :headers="loginAttemptsHeaders"
              :items="loginAttempts"
              :loading="loadingLoginAttempts"
              :items-per-page="loginAttemptsPagination.itemsPerPage"
              class="login-attempts-table"
              density="comfortable"
            >
              <!-- Success Status -->
              <template #[`item.success`]="{ item }">
                <v-chip
                  :color="item.success ? 'success' : 'error'"
                  size="small"
                  variant="tonal"
                >
                  <v-icon start size="small">
                    {{ item.success ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  {{ item.success ? 'Erfolg' : 'Fehlgeschlagen' }}
                </v-chip>
              </template>

              <!-- Username -->
              <template #[`item.username`]="{ item }">
                <div class="d-flex align-center">
                  <v-icon size="small" class="mr-2">mdi-account</v-icon>
                  <span>{{ item.username }}</span>
                  <v-chip
                    v-if="item.vorname || item.nachname"
                    size="x-small"
                    class="ml-2"
                    variant="outlined"
                  >
                    {{ item.vorname }} {{ item.nachname }}
                  </v-chip>
                </div>
              </template>

              <!-- IP Address -->
              <template #[`item.ip_address`]="{ item }">
                <div class="d-flex align-center">
                  <v-icon size="small" class="mr-2">mdi-ip-network</v-icon>
                  {{ item.ip_address }}
                </div>
              </template>

              <!-- Timestamp -->
              <template #[`item.created_at`]="{ item }">
                <div class="d-flex flex-column">
                  <span>{{ formatDate(item.created_at) }}</span>
                  <span class="text-caption text-grey">{{ formatTime(item.created_at) }}</span>
                </div>
              </template>

              <!-- Locked Until -->
              <template #[`item.locked_until`]="{ item }">
                <v-chip
                  v-if="item.locked_until && isLockActive(item.locked_until)"
                  color="error"
                  size="small"
                  variant="tonal"
                >
                  <v-icon start size="small">mdi-lock-clock</v-icon>
                  {{ formatDate(item.locked_until) }}
                </v-chip>
                <span v-else class="text-grey">-</span>
              </template>

              <!-- Actions -->
              <template #[`item.actions`]="{ item }">
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  color="info"
                  @click="showAttemptDetails(item)"
                >
                  <v-icon>mdi-information</v-icon>
                </v-btn>
                <v-btn
                  icon
                  variant="text"
                  size="small"
                  color="error"
                  @click="deleteLoginAttempt(item.id)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </div>

        <!-- ⚙️ Kategorienverwaltung -->
        <div
          v-else-if="activeTab === 'categories'"
          class="tab-content-container"
        >
          <div class="section-header">
            <div class="header-title">
              <v-icon color="success" size="28" class="mr-3"
                >mdi-shape-outline</v-icon
              >
              <h1>Kategorienverwaltung</h1>
            </div>
            <div class="header-actions">
              <v-btn
                color="success"
                variant="elevated"
                class="action-btn"
                @click="openAddCategoryDialog"
              >
                <v-icon start>mdi-plus</v-icon>
                Neue Kategorie
              </v-btn>
              <v-btn
                icon
                variant="text"
                color="success"
                class="refresh-btn"
                @click="fetchCategories"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>

          <v-divider class="admin-divider"></v-divider>

          <!-- Kategorien-Tabelle -->
          <div class="modern-table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th class="drag-column"></th>
                  <th>Name</th>
                  <th>Typ</th>
                  <th class="actions-column">Aktionen</th>
                </tr>
              </thead>
              <draggable
                v-model="categories"
                tag="tbody"
                handle=".drag-handle"
                item-key="id"
                @end="updateCategoryOrder"
              >
                <template #item="{ element }">
                  <tr :key="element.id">
                    <td class="drag-column">
                      <v-icon class="drag-handle" color="grey">mdi-drag</v-icon>
                    </td>
                    <td>{{ element.name }}</td>
                    <td>
                      <v-chip
                        size="small"
                        :color="getCategoryTypeColor(element.typ)"
                        variant="elevated"
                      >
                        {{ element.typ }}
                      </v-chip>
                    </td>
                    <td class="actions-column">
                      <v-btn
                        icon
                        variant="text"
                        color="success"
                        size="small"
                        class="action-icon-btn"
                        @click="openEditCategoryDialog(element)"
                      >
                        <v-icon size="small">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        variant="text"
                        color="error"
                        size="small"
                        class="action-icon-btn"
                        @click="confirmDeleteCategory(element.id)"
                      >
                        <v-icon size="small">mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </template>
              </draggable>
            </table>
          </div>
        </div>

        <!-- 💰 Preisverwaltung -->
        <div v-else-if="activeTab === 'preise'" class="tab-content-container">
          <div class="section-header">
            <div class="header-title">
              <v-icon color="success" size="28" class="mr-3"
                >mdi-currency-eur</v-icon
              >
              <h1>Preisverwaltung</h1>
            </div>
            <div class="header-actions">
              <v-btn
                color="success"
                variant="elevated"
                class="action-btn"
                @click="openAddPreisDialog"
              >
                <v-icon start>mdi-plus</v-icon>
                Neuer Preis
              </v-btn>

              <v-btn
                variant="text"
                :color="pricesVisible ? 'grey' : 'success'"
                class="toggle-btn"
                @click="togglePriceVisibility"
              >
                <v-icon start>{{
                  pricesVisible ? "mdi-eye-off" : "mdi-eye"
                }}</v-icon>
                {{ pricesVisible ? "Preise ausblenden" : "Preise anzeigen" }}
              </v-btn>

              <v-btn
                icon
                variant="text"
                color="success"
                class="refresh-btn"
                @click="fetchPreise"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>

          <v-divider class="admin-divider"></v-divider>

          <!-- Preise-Tabelle -->
          <div class="modern-table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>Reparatur-/Retourentyp</th>
                  <th>Gerätetyp</th>
                  <th v-if="pricesVisible">Preis (€)</th>
                  <th class="actions-column">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in preise" :key="item.id">
                  <td>{{ item.kategorie }}</td>
                  <td>{{ item.geraetetyp }}</td>
                  <td v-if="pricesVisible">
                    <span class="price-value">{{ item.preis }} €</span>
                  </td>
                  <td class="actions-column">
                    <v-btn
                      icon
                      variant="text"
                      color="success"
                      size="small"
                      class="action-icon-btn"
                      @click="openEditPreisDialog(item)"
                    >
                      <v-icon size="small">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      color="error"
                      size="small"
                      class="action-icon-btn"
                      @click="confirmDeletePreis(item.id)"
                    >
                      <v-icon size="small">mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ☎️ Durchwahlenverwaltung -->
        <div
          v-else-if="activeTab === 'durchwahlen'"
          class="tab-content-container"
        >
          <div class="section-header">
            <div class="header-title">
              <v-icon color="success" size="28" class="mr-3">mdi-phone</v-icon>
              <h1>Durchwahlenverwaltung</h1>
            </div>
            <div class="header-actions">
              <v-btn
                color="success"
                variant="elevated"
                class="action-btn"
                @click="openAddDurchwahlDialog"
              >
                <v-icon start>mdi-plus</v-icon>
                Neue Durchwahl
              </v-btn>
              <v-btn
                icon
                variant="text"
                color="success"
                class="refresh-btn"
                @click="fetchDurchwahlen"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>

          <v-divider class="admin-divider"></v-divider>

          <!-- Filterleiste -->
          <div class="filter-bar">
            <v-text-field
              v-model="durchwahlSearch"
              prepend-inner-icon="mdi-magnify"
              label="Durchwahlen durchsuchen"
              variant="outlined"
              density="comfortable"
              hide-details
              bg-color="rgba(30, 30, 40, 0.5)"
              class="search-field"
              clearable
            ></v-text-field>
          </div>

          <!-- Durchwahlen-Karten -->
          <div class="durchwahlen-grid">
            <div
              v-for="item in filteredDurchwahlen"
              :key="item.id"
              class="durchwahl-card"
            >
              <div class="durchwahl-card-content">
                <div class="durchwahl-number">{{ item.durchwahl }}</div>
                <div class="durchwahl-details">
                  <h3 class="durchwahl-name">
                    {{ item.name }}, {{ item.vorname }}
                  </h3>
                  <p class="durchwahl-abteilung">
                    <v-icon size="small" color="grey"
                      >mdi-office-building</v-icon
                    >
                    {{ item.abteilung || "Keine Abteilung" }}
                  </p>
                </div>
              </div>
              <div class="durchwahl-card-actions">
                <v-btn
                  icon
                  variant="text"
                  color="success"
                  size="small"
                  class="action-icon-btn"
                  @click="openEditDurchwahlDialog(item)"
                >
                  <v-icon size="small">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  variant="text"
                  color="error"
                  size="small"
                  class="action-icon-btn"
                  @click="confirmDeleteDurchwahl(item.id)"
                >
                  <v-icon size="small">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- 🔤 Kurztextverwaltung -->
        <div
          v-else-if="activeTab === 'kurztexte'"
          class="tab-content-container"
        >
          <div class="section-header">
            <div class="header-title">
              <v-icon color="success" size="28" class="mr-3"
                >mdi-text-short</v-icon
              >
              <h1>Kurztextverwaltung</h1>
            </div>
            <div class="header-actions">
              <v-btn
                color="success"
                variant="elevated"
                class="action-btn"
                @click="openAddKurztextDialog"
              >
                <v-icon start>mdi-plus</v-icon>
                Neuer Kurztext
              </v-btn>
              <v-btn
                icon
                variant="text"
                color="success"
                class="refresh-btn"
                @click="fetchKurztexte"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>

          <v-divider class="admin-divider"></v-divider>

          <!-- Kurztexte-Tabelle -->
          <div class="modern-table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th style="width: 100px">Nummer</th>
                  <th>Bezeichnung</th>
                  <th>Kurztext</th>
                  <th class="actions-column">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in kurztexte" :key="item.id">
                  <td>{{ item.nummer }}</td>
                  <td>{{ item.bezeichnung }}</td>
                  <td>{{ item.kurztext }}</td>
                  <td class="actions-column">
                    <v-btn
                      icon
                      variant="text"
                      color="success"
                      size="small"
                      class="action-icon-btn"
                      @click="openEditKurztextDialog(item)"
                    >
                      <v-icon size="small">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      color="error"
                      size="small"
                      class="action-icon-btn"
                      @click="confirmDeleteKurztext(item.id)"
                    >
                      <v-icon size="small">mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Tab-Content-Container für Announcements -->
        <div
          v-else-if="activeTab === 'announcements'"
          class="tab-content-container"
        >
          <div class="section-header">
            <div class="header-title">
              <v-icon color="success" size="28" class="mr-3"
                >mdi-bullhorn</v-icon
              >
              <h1>Ankündigungsverwaltung</h1>
            </div>
          </div>

          <v-divider class="admin-divider"></v-divider>

          <!-- Announcement-Editor integriert in den Tab -->
          <v-card class="announcement-editor-card">
            <v-card-title class="announcement-editor-title">
              <v-icon size="24" color="success" class="mr-2"
                >mdi-bullhorn</v-icon
              >
              <span>Ankündigungen erstellen</span>
            </v-card-title>

            <v-card-text>
              <v-tabs
                v-model="announcementTab"
                color="success"
                align-tabs="center"
                class="mb-6"
              >
                <v-tab value="editor">
                  <v-icon start>mdi-pencil</v-icon> Editor
                </v-tab>
                <v-tab value="preview">
                  <v-icon start>mdi-eye</v-icon> Vorschau
                </v-tab>
              </v-tabs>

              <v-window v-model="announcementTab">
                <v-window-item value="editor">
                  <v-radio-group
                    v-model="announcementMessage.type"
                    inline
                    class="mb-4"
                  >
                    <v-radio
                      label="Information"
                      color="info"
                      value="announcement"
                    ></v-radio>
                    <v-radio
                      label="Wichtige Warnung"
                      color="error"
                      value="warning"
                    ></v-radio>
                  </v-radio-group>

                  <v-textarea
                    v-model="announcementMessage.message"
                    label="Ankündigungstext"
                    variant="outlined"
                    counter
                    rows="5"
                    max-length="500"
                    clearable
                    placeholder="Geben Sie Ihre Ankündigung ein..."
                    hint="Diese Nachricht wird allen aktiven Benutzern angezeigt"
                    bg-color="rgba(20, 20, 30, 0.4)"
                  ></v-textarea>
                </v-window-item>

                <v-window-item value="preview">
                  <div class="announcement-preview-container">
                    <v-card
                      :class="[
                        'announcement-preview-card',
                        announcementMessage.type,
                      ]"
                      elevation="8"
                    >
                      <div class="announcement-preview-content">
                        <v-icon
                          :color="
                            announcementMessage.type === 'warning'
                              ? 'error'
                              : 'info'
                          "
                          size="24"
                          class="announcement-icon"
                        >
                          {{
                            announcementMessage.type === "warning"
                              ? "mdi-alert-circle"
                              : "mdi-bell-ring"
                          }}
                        </v-icon>

                        <div class="announcement-text-container">
                          <div class="announcement-title">
                            {{
                              announcementMessage.type === "warning"
                                ? "Wichtige Mitteilung"
                                : "Information"
                            }}
                          </div>
                          <div class="announcement-message">
                            {{
                              announcementMessage.message ||
                              "Deine Ankündigung wird hier erscheinen"
                            }}
                          </div>
                        </div>
                      </div>

                      <v-card-actions class="announcement-actions">
                        <v-btn
                          variant="text"
                          size="small"
                          color="default"
                          class="dismiss-btn"
                        >
                          Schließen
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn
                          v-if="announcementMessage.type === 'warning'"
                          variant="elevated"
                          color="error"
                          size="small"
                        >
                          Verstanden
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </div>
                </v-window-item>
              </v-window>
            </v-card-text>

            <v-card-actions class="announcement-editor-actions">
              <v-btn
                color="grey-darken-3"
                variant="elevated"
                @click="deactivateAnnouncement"
              >
                <v-icon start>mdi-bell-off</v-icon>
                Aktive Ankündigungen deaktivieren
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="success"
                variant="elevated"
                :disabled="!announcementMessage.message"
                @click="saveAnnouncement"
                size="large"
              >
                <v-icon start>mdi-send</v-icon>
                Ankündigung veröffentlichen
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </transition>
    </v-card>
    <!-- Dialog-Komponenten für die AdminView -->

    <!-- Registrierungsdialog für neue Nutzer - Moderne Version mit Tabs -->
    <v-dialog v-model="registerDialog" max-width="800" class="modern-dialog" persistent>
      <v-card class="dialog-card-modern register-user-dialog">
        <!-- Moderner Header -->
        <div class="dialog-header-modern">
          <div class="dialog-title-block">
            <div class="dialog-icon-wrapper register-icon">
              <v-icon color="success" size="28">mdi-account-plus</v-icon>
            </div>
            <div class="dialog-title-text">
              <h2 class="dialog-main-title">Neuen Nutzer registrieren</h2>
              <p class="dialog-subtitle">Alle Pflichtfelder (*) müssen ausgefüllt werden</p>
            </div>
          </div>
          <v-btn
            icon
            variant="text"
            color="grey"
            size="small"
            @click="registerDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="dialog-divider-modern"></v-divider>

        <!-- Tabs -->
        <v-tabs v-model="registerUserTab" class="edit-tabs" color="success" align-tabs="start">
          <v-tab value="basic">
            <v-icon start size="18">mdi-account-details</v-icon>
            Grunddaten
          </v-tab>
          <v-tab value="password">
            <v-icon start size="18">mdi-lock-plus</v-icon>
            Passwort
          </v-tab>
        </v-tabs>

        <v-divider class="dialog-divider-modern"></v-divider>

        <!-- Tab Content -->
        <div class="dialog-content-modern">
          <v-window v-model="registerUserTab">
            <!-- Grunddaten Tab -->
            <v-window-item value="basic">
              <div class="edit-section">
                <h3 class="section-heading">
                  <v-icon color="success" size="20" class="mr-2">mdi-account-circle</v-icon>
                  Persönliche Informationen
                </h3>
                <div class="modern-form-grid">
                  <v-text-field
                    v-model="newUser.loginname"
                    label="Benutzername *"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="success"
                    class="modern-input"
                    :rules="[v => !!v || 'Benutzername ist erforderlich']"
                    required
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="success" size="20">mdi-account-key</v-icon>
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-model="newUser.vorname"
                    label="Vorname *"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="success"
                    class="modern-input"
                    :rules="[v => !!v || 'Vorname ist erforderlich']"
                    required
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="success" size="20">mdi-account</v-icon>
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-model="newUser.nachname"
                    label="Nachname *"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="success"
                    class="modern-input"
                    :rules="[v => !!v || 'Nachname ist erforderlich']"
                    required
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="success" size="20">mdi-account</v-icon>
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-model="newUser.email"
                    label="E-Mail *"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="success"
                    class="modern-input"
                    type="email"
                    :rules="[
                      v => !!v || 'E-Mail ist erforderlich',
                      v => /.+@.+\..+/.test(v) || 'E-Mail muss gültig sein'
                    ]"
                    required
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="success" size="20">mdi-email</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </div>

              <v-divider class="section-divider"></v-divider>

              <div class="edit-section">
                <h3 class="section-heading">
                  <v-icon color="success" size="20" class="mr-2">mdi-briefcase</v-icon>
                  Arbeitsinformationen
                </h3>
                <div class="modern-form-grid">
                  <v-text-field
                    v-model="newUser.mitarbeiternummer"
                    label="Mitarbeiternummer *"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="success"
                    class="modern-input"
                    :rules="[v => !!v || 'Mitarbeiternummer ist erforderlich']"
                    required
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="success" size="20">mdi-badge-account</v-icon>
                    </template>
                  </v-text-field>

                  <v-select
                    v-model="newUser.abteilung"
                    :items="abteilungen"
                    label="Abteilung *"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="success"
                    class="modern-input"
                    :rules="[v => !!v || 'Abteilung ist erforderlich']"
                    required
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="success" size="20">mdi-office-building</v-icon>
                    </template>
                  </v-select>
                </div>

                <!-- Sammelaccount Toggle -->
                <div class="mt-4">
                  <v-switch
                    v-model="newUser.is_shared_account"
                    label="Als Sammelaccount markieren"
                    color="success"
                    hide-details
                    inset
                    density="comfortable"
                  >
                    <template v-slot:prepend>
                      <v-icon color="success" size="20">mdi-account-multiple</v-icon>
                    </template>
                  </v-switch>
                  <p class="text-caption text-grey mt-2 ml-10">
                    Sammelaccounts können von mehreren Mitarbeitern gleichzeitig genutzt werden.
                    Jede Session kann einen eigenen Status haben.
                  </p>
                </div>
              </div>

              <v-divider class="section-divider"></v-divider>

              <div class="edit-section">
                <h3 class="section-heading">
                  <v-icon color="success" size="20" class="mr-2">mdi-cake-variant</v-icon>
                  Geburtstag (optional)
                </h3>
                <div class="modern-form-grid">
                  <v-text-field
                    v-model="newUser.birthday"
                    type="date"
                    label="Geburtstag"
                    variant="outlined"
                    density="comfortable"
                    color="success"
                    class="modern-input"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    hint="Format: JJJJ-MM-TT"
                    persistent-hint
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="success" size="20">mdi-calendar</v-icon>
                    </template>
                  </v-text-field>

                  <div class="modern-input d-flex align-center">
                    <v-switch
                      v-model="newUser.birthday_public"
                      label="Geburtstag öffentlich"
                      color="success"
                      hide-details
                      inset
                      density="comfortable"
                    >
                      <template v-slot:prepend>
                        <v-icon color="success" size="20">mdi-eye</v-icon>
                      </template>
                    </v-switch>
                  </div>
                </div>
              </div>
            </v-window-item>

            <!-- Passwort Tab -->
            <v-window-item value="password">
              <div class="edit-section">
                <v-alert
                  type="warning"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                >
                  <v-icon start>mdi-shield-alert</v-icon>
                  Bitte wählen Sie ein sicheres Passwort, das alle Anforderungen erfüllt.
                </v-alert>

                <div class="modern-form-grid">
                  <v-text-field
                    v-model="newUser.passwort"
                    label="Passwort *"
                    type="password"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="success"
                    class="modern-input"
                    :rules="[v => !!v || 'Passwort ist erforderlich']"
                    required
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="success" size="20">mdi-lock</v-icon>
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-model="newUser.passwortRepeat"
                    label="Passwort bestätigen *"
                    type="password"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="success"
                    class="modern-input"
                    :rules="[
                      v => !!v || 'Passwortbestätigung ist erforderlich',
                      v => v === newUser.passwort || 'Passwörter stimmen nicht überein'
                    ]"
                    required
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="success" size="20">mdi-lock-check</v-icon>
                    </template>
                  </v-text-field>
                </div>

                <!-- Passwortanforderungen -->
                <div class="password-requirements-modern">
                  <h4 class="requirements-heading">Passwortanforderungen</h4>
                  <div class="requirements-list">
                    <div class="requirement-item" :class="{ met: newUserPasswordMinLength }">
                      <v-icon
                        size="18"
                        :color="newUserPasswordMinLength ? 'success' : 'grey'"
                      >
                        {{ newUserPasswordMinLength ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Mindestens 8 Zeichen</span>
                    </div>

                    <div class="requirement-item" :class="{ met: newUserPasswordUpperCase }">
                      <v-icon
                        size="18"
                        :color="newUserPasswordUpperCase ? 'success' : 'grey'"
                      >
                        {{ newUserPasswordUpperCase ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Mindestens ein Großbuchstabe</span>
                    </div>

                    <div class="requirement-item" :class="{ met: newUserPasswordLowerCase }">
                      <v-icon
                        size="18"
                        :color="newUserPasswordLowerCase ? 'success' : 'grey'"
                      >
                        {{ newUserPasswordLowerCase ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Mindestens ein Kleinbuchstabe</span>
                    </div>

                    <div class="requirement-item" :class="{ met: newUserPasswordSpecialChar }">
                      <v-icon
                        size="18"
                        :color="newUserPasswordSpecialChar ? 'success' : 'grey'"
                      >
                        {{ newUserPasswordSpecialChar ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Mindestens ein Sonderzeichen</span>
                    </div>

                    <div class="requirement-item" :class="{ met: newUserPasswordsMatch }">
                      <v-icon
                        size="18"
                        :color="newUserPasswordsMatch ? 'success' : 'grey'"
                      >
                        {{ newUserPasswordsMatch ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Passwörter stimmen überein</span>
                    </div>
                  </div>
                </div>
              </div>
            </v-window-item>
          </v-window>
        </div>

        <v-divider class="dialog-divider-modern"></v-divider>

        <!-- Footer -->
        <div class="dialog-actions-modern">
          <v-btn
            variant="text"
            color="grey"
            @click="registerDialog = false"
            class="modern-btn-text"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            @click="registerUser"
            :disabled="!newUserPasswordValid"
            class="modern-btn-success"
          >
            <v-icon start>mdi-account-check</v-icon>
            Registrieren
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Bearbeitungsdialog für Nutzer - Moderne Version mit Tabs -->
    <v-dialog v-model="editDialog" max-width="800" class="modern-dialog" persistent>
      <v-card class="dialog-card-modern edit-user-dialog">
        <!-- Moderner Header -->
        <div class="dialog-header-modern">
          <div class="dialog-title-block">
            <div class="dialog-icon-wrapper">
              <v-icon color="primary" size="28">mdi-account-edit</v-icon>
            </div>
            <div class="dialog-title-text">
              <h2 class="dialog-main-title">Nutzer bearbeiten</h2>
              <p class="dialog-subtitle" v-if="editUser.vorname">{{ editUser.vorname }} {{ editUser.nachname }} (@{{ editUser.loginname }})</p>
            </div>
          </div>
          <v-btn
            icon
            variant="text"
            color="grey"
            size="small"
            @click="editDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="dialog-divider-modern"></v-divider>

        <!-- Tabs -->
        <v-tabs v-model="editUserTab" class="edit-tabs" color="primary" align-tabs="start">
          <v-tab value="basic">
            <v-icon start size="18">mdi-account-details</v-icon>
            Grunddaten
          </v-tab>
          <v-tab value="password">
            <v-icon start size="18">mdi-lock-reset</v-icon>
            Passwort
          </v-tab>
        </v-tabs>

        <v-divider class="dialog-divider-modern"></v-divider>

        <!-- Tab Content -->
        <div class="dialog-content-modern">
          <v-window v-model="editUserTab">
            <!-- Grunddaten Tab -->
            <v-window-item value="basic">
              <div class="edit-section">
                <h3 class="section-heading">
                  <v-icon color="primary" size="20" class="mr-2">mdi-account-circle</v-icon>
                  Persönliche Informationen
                </h3>
                <div class="modern-form-grid">
                  <v-text-field
                    v-model="editUser.loginname"
                    label="Loginname"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="grey"
                    class="modern-input"
                    readonly
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="grey" size="20">mdi-account-key</v-icon>
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-model="editUser.vorname"
                    label="Vorname"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="primary"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-account</v-icon>
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-model="editUser.nachname"
                    label="Nachname"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="primary"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-account</v-icon>
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-model="editUser.email"
                    label="E-Mail"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="primary"
                    class="modern-input"
                    type="email"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-email</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </div>

              <v-divider class="section-divider"></v-divider>

              <div class="edit-section">
                <h3 class="section-heading">
                  <v-icon color="primary" size="20" class="mr-2">mdi-briefcase</v-icon>
                  Arbeitsinformationen
                </h3>
                <div class="modern-form-grid">
                  <v-text-field
                    v-model="editUser.mitarbeiternummer"
                    label="Mitarbeiternummer"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="primary"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-badge-account</v-icon>
                    </template>
                  </v-text-field>

                  <v-select
                    v-model="editUser.abteilung"
                    :items="abteilungen"
                    label="Abteilung"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="primary"
                    class="modern-input"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-office-building</v-icon>
                    </template>
                  </v-select>
                </div>

                <!-- Sammelaccount Toggle -->
                <div class="mt-4">
                  <v-switch
                    v-model="editUser.is_shared_account"
                    label="Als Sammelaccount markieren"
                    color="primary"
                    hide-details
                    inset
                    density="comfortable"
                  >
                    <template v-slot:prepend>
                      <v-icon color="primary" size="20">mdi-account-multiple</v-icon>
                    </template>
                  </v-switch>
                  <p class="text-caption text-grey mt-2 ml-10">
                    Sammelaccounts können von mehreren Mitarbeitern gleichzeitig genutzt werden.
                    Jede Session kann einen eigenen Status haben.
                  </p>
                </div>
              </div>

              <v-divider class="section-divider"></v-divider>

              <div class="edit-section">
                <h3 class="section-heading">
                  <v-icon color="primary" size="20" class="mr-2">mdi-cake-variant</v-icon>
                  Geburtstag (optional)
                </h3>
                <div class="modern-form-grid">
                  <v-text-field
                    v-model="editUser.birthday"
                    type="date"
                    label="Geburtstag"
                    variant="outlined"
                    density="comfortable"
                    color="primary"
                    class="modern-input"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    hint="Format: JJJJ-MM-TT"
                    persistent-hint
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-calendar</v-icon>
                    </template>
                  </v-text-field>

                  <div class="modern-input d-flex align-center">
                    <v-switch
                      v-model="editUser.birthday_public"
                      label="Geburtstag öffentlich"
                      color="primary"
                      hide-details
                      inset
                      density="comfortable"
                    >
                      <template v-slot:prepend>
                        <v-icon color="primary" size="20">mdi-eye</v-icon>
                      </template>
                    </v-switch>
                  </div>
                </div>
              </div>
            </v-window-item>

            <!-- Passwort Tab -->
            <v-window-item value="password">
              <div class="edit-section">
                <v-alert
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                >
                  <v-icon start>mdi-information</v-icon>
                  Lassen Sie die Felder leer, um das Passwort nicht zu ändern.
                </v-alert>

                <div class="modern-form-grid">
                  <v-text-field
                    v-model="newPassword"
                    label="Neues Passwort"
                    type="password"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="primary"
                    class="modern-input"
                    clearable
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-lock</v-icon>
                    </template>
                  </v-text-field>

                  <v-text-field
                    v-model="confirmPassword"
                    label="Passwort bestätigen"
                    type="password"
                    variant="outlined"
                    density="comfortable"
                    bg-color="rgba(30, 30, 40, 0.5)"
                    color="primary"
                    class="modern-input"
                    :disabled="!newPassword"
                    clearable
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="primary" size="20">mdi-lock-check</v-icon>
                    </template>
                  </v-text-field>
                </div>

                <!-- Passwortanforderungen -->
                <div class="password-requirements-modern" v-if="newPassword">
                  <h4 class="requirements-heading">Passwortanforderungen</h4>
                  <div class="requirements-list">
                    <div class="requirement-item" :class="{ met: editPasswordMinLength }">
                      <v-icon
                        size="18"
                        :color="editPasswordMinLength ? 'success' : 'grey'"
                      >
                        {{ editPasswordMinLength ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Mindestens 8 Zeichen</span>
                    </div>

                    <div class="requirement-item" :class="{ met: editPasswordUpperCase }">
                      <v-icon
                        size="18"
                        :color="editPasswordUpperCase ? 'success' : 'grey'"
                      >
                        {{ editPasswordUpperCase ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Mindestens ein Großbuchstabe</span>
                    </div>

                    <div class="requirement-item" :class="{ met: editPasswordLowerCase }">
                      <v-icon
                        size="18"
                        :color="editPasswordLowerCase ? 'success' : 'grey'"
                      >
                        {{ editPasswordLowerCase ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Mindestens ein Kleinbuchstabe</span>
                    </div>

                    <div class="requirement-item" :class="{ met: editPasswordSpecialChar }">
                      <v-icon
                        size="18"
                        :color="editPasswordSpecialChar ? 'success' : 'grey'"
                      >
                        {{ editPasswordSpecialChar ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Mindestens ein Sonderzeichen</span>
                    </div>

                    <div class="requirement-item" :class="{ met: editPasswordsMatch }">
                      <v-icon
                        size="18"
                        :color="editPasswordsMatch ? 'success' : 'grey'"
                      >
                        {{ editPasswordsMatch ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                      </v-icon>
                      <span>Passwörter stimmen überein</span>
                    </div>
                  </div>
                </div>
              </div>
            </v-window-item>
          </v-window>
        </div>

        <v-divider class="dialog-divider-modern"></v-divider>

        <!-- Footer -->
        <div class="dialog-actions-modern">
          <v-btn
            variant="text"
            color="grey"
            @click="editDialog = false"
            class="modern-btn-text"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="saveUserChanges"
            :disabled="newPassword.length > 0 && !editPasswordValid"
            class="modern-btn-primary"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Kategorien: Hinzufügen/Bearbeiten -->
    <v-dialog v-model="categoryDialog" max-width="500" class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header">
          <v-icon color="primary" size="24" class="mr-3">
            {{ categoryEditMode ? "mdi-shape-circle-plus" : "mdi-shape-plus" }}
          </v-icon>
          <h2 class="dialog-title">
            {{
              categoryEditMode
                ? "Kategorie bearbeiten"
                : "Neue Kategorie hinzufügen"
            }}
          </h2>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-content">
          <v-select
            v-model="categoryItem.typ"
            :items="['Gerätetyp', 'Reparaturtyp', 'Retourentyp']"
            label="Kategorie-Typ"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="primary"
            class="mb-4"
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" size="small">mdi-shape</v-icon>
            </template>
          </v-select>

          <v-text-field
            v-model="categoryItem.name"
            label="Kategoriename"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="primary"
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" size="small">mdi-label</v-icon>
            </template>
          </v-text-field>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-actions">
          <v-btn
            variant="text"
            class="cancel-btn"
            @click="categoryDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            class="submit-btn"
            @click="saveCategory"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Preise: Hinzufügen/Bearbeiten -->
    <v-dialog v-model="preisDialog" max-width="500" class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header">
          <v-icon color="primary" size="24" class="mr-3">
            {{ preisEditMode ? "mdi-cash-edit" : "mdi-cash-plus" }}
          </v-icon>
          <h2 class="dialog-title">
            {{ preisEditMode ? "Preis bearbeiten" : "Neuen Preis hinzufügen" }}
          </h2>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-content">
          <v-select
            v-model="preisItem.kategorie_id"
            :items="
              kategorien.filter(
                (k) => k.typ === 'Reparaturtyp' || k.typ === 'Retourentyp'
              )
            "
            label="Reparatur-/Retourentyp"
            item-title="name"
            item-value="id"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="primary"
            class="mb-4"
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" size="small">mdi-wrench</v-icon>
            </template>
          </v-select>

          <v-select
            v-model="preisItem.geraetetyp_id"
            :items="geraetetypen"
            label="Gerätetyp"
            item-title="name"
            item-value="id"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="primary"
            class="mb-4"
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" size="small">mdi-devices</v-icon>
            </template>
          </v-select>

          <v-text-field
            v-model="preisItem.preis"
            label="Preis (€)"
            type="number"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="primary"
            suffix="€"
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" size="small">mdi-currency-eur</v-icon>
            </template>
          </v-text-field>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-actions">
          <v-btn variant="text" class="cancel-btn" @click="preisDialog = false">
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            class="submit-btn"
            @click="savePreis"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Durchwahlen: Hinzufügen/Bearbeiten -->
    <v-dialog v-model="durchwahlDialog" max-width="500" class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header">
          <v-icon color="primary" size="24" class="mr-3">
            {{ durchwahlEditMode ? "mdi-phone-edit" : "mdi-phone-plus" }}
          </v-icon>
          <h2 class="dialog-title">
            {{
              durchwahlEditMode
                ? "Durchwahl bearbeiten"
                : "Neue Durchwahl hinzufügen"
            }}
          </h2>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-content">
          <v-text-field
            v-model="durchwahlItem.name"
            label="Name"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="primary"
            class="mb-4"
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" size="small">mdi-account</v-icon>
            </template>
          </v-text-field>

          <v-text-field
            v-model="durchwahlItem.vorname"
            label="Vorname"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="primary"
            class="mb-4"
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" size="small">mdi-account</v-icon>
            </template>
          </v-text-field>

          <v-text-field
            v-model="durchwahlItem.durchwahl"
            label="Durchwahl"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="primary"
            class="mb-4"
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" size="small">mdi-phone</v-icon>
            </template>
          </v-text-field>

          <v-text-field
            v-model="durchwahlItem.abteilung"
            label="Abteilung (optional)"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="primary"
          >
            <template v-slot:prepend-inner>
              <v-icon color="primary" size="small">mdi-office-building</v-icon>
            </template>
          </v-text-field>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-actions">
          <v-btn
            variant="text"
            class="cancel-btn"
            @click="durchwahlDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            class="submit-btn"
            @click="saveDurchwahl"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Kurztexte: Hinzufügen/Bearbeiten -->
    <v-dialog v-model="kurztextDialog" max-width="500" class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header">
          <v-icon size="large" color="primary">mdi-text-short</v-icon>
          <h2 class="dialog-title">
            {{
              kurztextEditMode
                ? "Kurztext bearbeiten"
                : "Neuen Kurztext hinzufügen"
            }}
          </h2>
        </div>
        <v-divider class="dialog-divider"></v-divider>
        <div class="dialog-content">
          <div class="form-grid">
            <v-text-field
              v-model="kurztextItem.nummer"
              label="Nummer"
              type="number"
              variant="outlined"
              hint="Nummer des Kurztextes"
              persistent-hint
            ></v-text-field>

            <!-- Neues Feld für die Bezeichnung -->
            <v-text-field
              v-model="kurztextItem.bezeichnung"
              label="Bezeichnung"
              variant="outlined"
              hint="Kurze Beschreibung (optional)"
              persistent-hint
            ></v-text-field>

            <v-textarea
              v-model="kurztextItem.kurztext"
              label="Kurztext"
              variant="outlined"
              rows="6"
              auto-grow
              class="mt-3"
              hint="Inhalt des Kurztextes"
              persistent-hint
            ></v-textarea>
          </div>
        </div>
        <v-divider class="dialog-divider"></v-divider>
        <div class="dialog-actions">
          <v-btn variant="text" color="error" @click="kurztextDialog = false">
            Abbrechen
          </v-btn>
          <v-btn variant="elevated" color="primary" @click="saveKurztext">
            {{ kurztextEditMode ? "Speichern" : "Hinzufügen" }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Bestätigungsdialoge für das Löschen -->

    <!-- Benutzer löschen -->
    <v-dialog v-model="deleteDialog" max-width="450" class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header warning-header">
          <v-icon color="error" size="24" class="mr-3">mdi-alert</v-icon>
          <h2 class="dialog-title">Benutzer löschen</h2>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-content">
          <div class="warning-message">
            <p>Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?</p>
            <p class="warning-details">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
          </div>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-actions">
          <v-btn
            variant="text"
            class="cancel-btn"
            @click="deleteDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            class="delete-btn"
            @click="deleteUser"
          >
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Login-Attempt Details Dialog -->
    <v-dialog v-model="loginAttemptsDetailDialog" max-width="700" class="modern-dialog">
      <v-card class="dialog-card-modern" v-if="selectedLoginAttempt">
        <div class="dialog-header-modern">
          <div class="dialog-title-block">
            <div class="dialog-icon-wrapper">
              <v-icon :color="selectedLoginAttempt.success ? 'success' : 'error'" size="28">
                {{ selectedLoginAttempt.success ? 'mdi-check-circle' : 'mdi-alert-circle' }}
              </v-icon>
            </div>
            <div class="dialog-title-text">
              <h2 class="dialog-main-title">Login-Versuch Details</h2>
              <p class="dialog-subtitle">
                {{ selectedLoginAttempt.success ? 'Erfolgreicher' : 'Fehlgeschlagener' }} Login-Versuch
              </p>
            </div>
          </div>
          <v-btn
            icon
            variant="text"
            color="grey"
            size="small"
            @click="loginAttemptsDetailDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="dialog-divider-modern"></v-divider>

        <div class="dialog-content-modern">
          <!-- Benutzer-Informationen -->
          <div class="detail-section">
            <h3 class="detail-section-title">
              <v-icon size="20" class="mr-2">mdi-account</v-icon>
              Benutzer-Informationen
            </h3>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Username:</span>
                <span class="detail-value">{{ selectedLoginAttempt.username }}</span>
              </div>
              <div class="detail-item" v-if="selectedLoginAttempt.vorname || selectedLoginAttempt.nachname">
                <span class="detail-label">Name:</span>
                <span class="detail-value">
                  {{ selectedLoginAttempt.vorname }} {{ selectedLoginAttempt.nachname }}
                </span>
              </div>
              <div class="detail-item" v-if="selectedLoginAttempt.email">
                <span class="detail-label">E-Mail:</span>
                <span class="detail-value">{{ selectedLoginAttempt.email }}</span>
              </div>
              <div class="detail-item" v-if="selectedLoginAttempt.abteilung">
                <span class="detail-label">Abteilung:</span>
                <span class="detail-value">{{ selectedLoginAttempt.abteilung }}</span>
              </div>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Technische Details -->
          <div class="detail-section">
            <h3 class="detail-section-title">
              <v-icon size="20" class="mr-2">mdi-information</v-icon>
              Technische Details
            </h3>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Status:</span>
                <v-chip
                  :color="selectedLoginAttempt.success ? 'success' : 'error'"
                  size="small"
                  variant="tonal"
                >
                  <v-icon start size="small">
                    {{ selectedLoginAttempt.success ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  {{ selectedLoginAttempt.success ? 'Erfolgreich' : 'Fehlgeschlagen' }}
                </v-chip>
              </div>
              <div class="detail-item">
                <span class="detail-label">IP-Adresse:</span>
                <span class="detail-value">
                  <v-icon size="small" class="mr-1">mdi-ip-network</v-icon>
                  {{ selectedLoginAttempt.ip_address }}
                </span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Zeitstempel:</span>
                <span class="detail-value">
                  <v-icon size="small" class="mr-1">mdi-calendar-clock</v-icon>
                  {{ formatDate(selectedLoginAttempt.created_at) }}
                  {{ formatTime(selectedLoginAttempt.created_at) }}
                </span>
              </div>
              <div class="detail-item" v-if="selectedLoginAttempt.locked_until">
                <span class="detail-label">Gesperrt bis:</span>
                <v-chip
                  :color="isLockActive(selectedLoginAttempt.locked_until) ? 'error' : 'grey'"
                  size="small"
                  variant="tonal"
                >
                  <v-icon start size="small">mdi-lock-clock</v-icon>
                  {{ formatDate(selectedLoginAttempt.locked_until) }}
                  {{ formatTime(selectedLoginAttempt.locked_until) }}
                </v-chip>
              </div>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- User Agent -->
          <div class="detail-section">
            <h3 class="detail-section-title">
              <v-icon size="20" class="mr-2">mdi-devices</v-icon>
              Browser/Gerät
            </h3>
            <div class="user-agent-box">
              {{ selectedLoginAttempt.user_agent || 'Keine Informationen verfügbar' }}
            </div>
          </div>
        </div>

        <v-divider class="dialog-divider-modern"></v-divider>

        <div class="dialog-actions-modern">
          <v-btn
            variant="text"
            color="grey"
            @click="loginAttemptsDetailDialog = false"
            class="modern-btn-text"
          >
            Schließen
          </v-btn>
          <v-btn
            color="error"
            variant="outlined"
            @click="deleteLoginAttempt(selectedLoginAttempt.id); loginAttemptsDetailDialog = false"
            class="modern-btn-text"
          >
            <v-icon start>mdi-delete</v-icon>
            Eintrag löschen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Kategorie löschen -->
    <v-dialog
      v-model="deleteCategoryDialog"
      max-width="450"
      class="modern-dialog"
    >
      <v-card class="dialog-card">
        <div class="dialog-header warning-header">
          <v-icon color="error" size="24" class="mr-3">mdi-alert</v-icon>
          <h2 class="dialog-title">Kategorie löschen</h2>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-content">
          <div class="warning-message">
            <p>Sind Sie sicher, dass Sie diese Kategorie löschen möchten?</p>
            <p class="warning-details">
              Diese Aktion kann nicht rückgängig gemacht werden und könnte auch
              zugehörige Daten betreffen.
            </p>
          </div>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-actions">
          <v-btn
            variant="text"
            class="cancel-btn"
            @click="deleteCategoryDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            class="delete-btn"
            @click="deleteCategory"
          >
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Durchwahl löschen -->
    <v-dialog
      v-model="deleteDurchwahlDialog"
      max-width="450"
      class="modern-dialog"
    >
      <v-card class="dialog-card">
        <div class="dialog-header warning-header">
          <v-icon color="error" size="24" class="mr-3">mdi-alert</v-icon>
          <h2 class="dialog-title">Durchwahl löschen</h2>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-content">
          <div class="warning-message">
            <p>Sind Sie sicher, dass Sie diese Durchwahl löschen möchten?</p>
            <p class="warning-details">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
          </div>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-actions">
          <v-btn
            variant="text"
            class="cancel-btn"
            @click="deleteDurchwahlDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            class="delete-btn"
            @click="deleteDurchwahl"
          >
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Kurztext löschen -->
    <v-dialog
      v-model="deleteKurztextDialog"
      max-width="450"
      class="modern-dialog"
    >
      <v-card class="dialog-card">
        <div class="dialog-header warning-header">
          <v-icon color="error" size="24" class="mr-3">mdi-alert</v-icon>
          <h2 class="dialog-title">Kurztext löschen</h2>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-content">
          <div class="warning-message">
            <p>Sind Sie sicher, dass Sie diesen Kurztext löschen möchten?</p>
            <p class="warning-details">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
          </div>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-actions">
          <v-btn
            variant="text"
            class="cancel-btn"
            @click="deleteKurztextDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            class="delete-btn"
            @click="deleteKurztext"
          >
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Benutzer entsperren -->
    <v-dialog v-model="unlockDialog" max-width="500" class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header success-header">
          <v-icon color="success" size="24" class="mr-3"
            >mdi-lock-open</v-icon
          >
          <h2 class="dialog-title">Benutzer entsperren</h2>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-content">
          <div class="unlock-message">
            <p>
              Möchten Sie die Sperre für
              <strong>{{ unlockItem?.username }}</strong> wirklich aufheben?
            </p>
            <div v-if="unlockItem" class="unlock-details">
              <v-chip color="error" size="small" variant="tonal" class="mb-2">
                <v-icon start size="small">mdi-account</v-icon>
                {{ unlockItem.username }}
              </v-chip>
              <v-chip color="warning" size="small" variant="tonal" class="mb-2 ml-2">
                <v-icon start size="small">mdi-ip-network</v-icon>
                {{ unlockItem.ip_address }}
              </v-chip>
              <div class="mt-3">
                <p class="text-body-2">
                  <v-icon size="small" color="warning">mdi-alert-circle</v-icon>
                  Fehlversuche: {{ unlockItem.failed_attempts }}
                </p>
                <p class="text-body-2 mt-1">
                  <v-icon size="small" color="info">mdi-clock</v-icon>
                  Verbleibende Zeit:
                  {{ formatRemainingTime(unlockItem.remaining_minutes) }}
                </p>
              </div>
            </div>
            <p class="warning-details mt-3">
              Nach dem Entsperren kann sich der Benutzer sofort wieder
              anmelden.
            </p>
          </div>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-actions">
          <v-btn
            variant="text"
            class="cancel-btn"
            @click="unlockDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            class="submit-btn"
            @click="unlockUser"
          >
            <v-icon start>mdi-lock-open</v-icon>
            Entsperren
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog: Login-Verlauf anzeigen -->
    <v-dialog v-model="historyDialog" max-width="1000" class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header">
          <v-icon color="info" size="24" class="mr-3">mdi-history</v-icon>
          <h2 class="dialog-title">Login-Verlauf</h2>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-content" v-if="historyItem">
          <div class="history-header">
            <v-chip color="error" size="small" variant="tonal">
              <v-icon start size="small">mdi-account</v-icon>
              {{ historyItem.username }}
            </v-chip>
            <v-chip color="warning" size="small" variant="tonal" class="ml-2">
              <v-icon start size="small">mdi-ip-network</v-icon>
              {{ historyItem.ip_address }}
            </v-chip>
          </div>

          <!-- Loading Indicator -->
          <div v-if="loadingHistory" class="text-center pa-8">
            <v-progress-circular
              indeterminate
              color="info"
              size="48"
            ></v-progress-circular>
            <p class="mt-4">Lade Login-Verlauf...</p>
          </div>

          <!-- History Table -->
          <div v-else-if="loginHistory.length > 0" class="history-table-container">
            <v-table class="history-table" density="comfortable">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Status</th>
                  <th>IP-Adresse</th>
                  <th>Benutzername</th>
                  <th>Gerät/Browser</th>
                  <th>Sperre bis</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="entry in loginHistory"
                  :key="entry.id"
                  :class="{ 'failed-attempt': !entry.success, 'success-attempt': entry.success }"
                >
                  <td class="text-body-2">
                    {{ formatAttemptTime(entry.attempt_time) }}
                  </td>
                  <td>
                    <v-chip
                      :color="entry.success ? 'success' : 'error'"
                      size="small"
                      variant="tonal"
                    >
                      <v-icon
                        start
                        size="small"
                        >{{ entry.success ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon
                      >
                      {{ entry.success ? 'Erfolg' : 'Fehlgeschlagen' }}
                    </v-chip>
                  </td>
                  <td class="text-body-2">{{ entry.ip_address }}</td>
                  <td class="text-body-2">{{ entry.username }}</td>
                  <td class="text-caption">{{ entry.user_agent || '-' }}</td>
                  <td class="text-body-2">
                    <span v-if="entry.locked_until">
                      {{ formatAttemptTime(entry.locked_until) }}
                    </span>
                    <span v-else class="text-disabled">-</span>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <!-- No History -->
          <v-alert v-else type="info" variant="tonal" class="ma-4">
            <v-icon start>mdi-information</v-icon>
            Keine Login-Versuche gefunden.
          </v-alert>
        </div>

        <v-divider class="dialog-divider"></v-divider>

        <div class="dialog-actions">
          <v-btn
            variant="text"
            class="cancel-btn"
            @click="historyDialog = false"
          >
            Schließen
          </v-btn>
          <v-btn
            color="info"
            variant="elevated"
            @click="fetchLoginHistory(historyItem)"
            :loading="loadingHistory"
          >
            <v-icon start>mdi-refresh</v-icon>
            Aktualisieren
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="sessionsDialog" max-width="900" class="modern-dialog">
      <v-card class="dialog-card user-card">
        <div class="card-header">
          <div class="title-container">
            <v-icon color="primary" size="24" class="mr-3"
              >mdi-account-multiple-check</v-icon
            >
            <h2 class="card-title">
              Aktive Sessions: {{ selectedUser?.loginname }}
            </h2>
          </div>
          <div class="mb-2">
            <span class="font-weight-medium">Letzter Login:</span>
            {{
              selectedUser.last_login
                ? formatDateTime(selectedUser.last_login)
                : "Noch nie angemeldet"
            }}
          </div>
          <v-btn icon variant="text" @click="sessionsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="card-divider"></v-divider>

        <v-card-text>
          <!-- Hier "loading" zu "loadingSessions" ändern -->
          <div v-if="loadingSessions" class="text-center pa-4">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
            <div class="mt-2">Lade Sessions...</div>
          </div>

          <div v-else-if="userSessions.length === 0" class="text-center pa-4">
            <v-icon size="48" color="grey">mdi-logout-variant</v-icon>
            <div class="mt-2 text-grey">Keine aktiven Sessions vorhanden</div>
          </div>

          <div v-else class="sessions-container">
            <v-table>
              <thead>
                <tr>
                  <th>Geräteinformation</th>
                  <th>IP-Adresse</th>
                  <th>Status</th>
                  <th>Letzte Aktivität</th>
                  <th>Erstellt am</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="session in userSessions" :key="session.id">
                  <td>
                    <div class="device-info">
                      <v-icon size="small" class="mr-2">{{
                        getDeviceIcon(session.device_info)
                      }}</v-icon>
                      {{ formatDeviceInfo(session.device_info) }}
                    </div>
                  </td>
                  <td>{{ session.ip_address }}</td>
                  <td>
                    <v-chip
                      :color="getStatusColor(session.user_status)"
                      size="small"
                    >
                      {{ getStatusText(session.user_status) }}
                    </v-chip>
                  </td>
                  <td>{{ formatDateTime(session.last_activity) }}</td>
                  <td>{{ formatDateTime(session.created_at) }}</td>
                  <td>
                    <v-btn
                      size="small"
                      color="error"
                      variant="text"
                      @click="terminateSession(session.id)"
                    >
                      <v-icon start size="small">mdi-logout</v-icon>
                      Abmelden
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </v-card-text>

        <v-divider class="card-divider"></v-divider>

        <v-card-actions class="d-flex justify-space-between">
          <v-btn variant="text" @click="sessionsDialog = false"
            >Schließen</v-btn
          >
          <v-btn
            color="error"
            variant="elevated"
            @click="terminateAllUserSessions"
            :disabled="userSessions.length === 0"
          >
            <v-icon start>mdi-logout-variant</v-icon>
            Alle Sessions beenden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</v-container>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import axios from "axios";
import { API_BASE_URL, WS_URL } from "../config";
import { useAuthStore } from "@/stores/auth";
import { useToast } from "vue-toastification";
import draggable from "vuedraggable";
import SharedAccountsTab from "@/components/admin/SharedAccountsTab.vue";

const toast = useToast();
const authStore = useAuthStore();

// ======== Allgemeine Zustandsvariablen ========
const activeTab = ref("users");
const tabItems = ref([
  { title: "Nutzerverwaltung", value: "users", icon: "mdi-account-group" },
  { title: "Sammelaccounts", value: "shared-accounts", icon: "mdi-account-multiple" },
  { title: "Gesperrte Benutzer", value: "locked", icon: "mdi-lock-alert" },
  { title: "Login-Versuche", value: "login-attempts", icon: "mdi-shield-account" },
  { title: "Kategorien", value: "categories", icon: "mdi-shape-outline" },
  { title: "Preise", value: "preise", icon: "mdi-currency-eur" },
  { title: "Durchwahlen", value: "durchwahlen", icon: "mdi-phone" },
  { title: "Kurztexte", value: "kurztexte", icon: "mdi-text-short" },
  { title: "Announcements", value: "announcements", icon: "mdi-bullhorn" },
]);

// ======== Nutzerverwaltung ========
const users = ref([]);
const userSearch = ref("");
const selectedUserFilters = ref([]);

// Nutzerdialoge
const registerDialog = ref(false);
const registerUserTab = ref("basic"); // Tab-Steuerung für Register-Dialog
const editDialog = ref(false);
const editUserTab = ref("basic"); // Tab-Steuerung für Edit-Dialog
const deleteDialog = ref(false);
const deleteUserId = ref(null);

// Neue Nutzer und Bearbeiten
const newUser = ref({
  loginname: "",
  vorname: "",
  nachname: "",
  email: "",
  mitarbeiternummer: "",
  abteilung: "Support",
  passwort: "",
  passwortRepeat: "",
  birthday: "",
  birthday_public: false,
  is_shared_account: false,
});

const editUser = ref({});
const newPassword = ref("");
const confirmPassword = ref("");

// Abteilungen für Auswahlfelder
const abteilungen = ref([
  "Geschäftsleitung",
  "Support",
  "KC1",
  "KC2",
  "KC3",
  "KC4",
  "Retouren",
  "Lager",
  "Wareneingang/-ausgang",
]);

// Gefilterte Nutzer basierend auf Suche und ausgewählten Filtern
const filteredUsers = computed(() => {
  let result = [...users.value];

  // Textsuche anwenden
  if (userSearch.value) {
    const searchTerm = userSearch.value.toLowerCase();
    result = result.filter(
      (user) =>
        user.loginname.toLowerCase().includes(searchTerm) ||
        user.vorname.toLowerCase().includes(searchTerm) ||
        user.nachname.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm) ||
        user.mitarbeiternummer.toLowerCase().includes(searchTerm)
    );
  }

  // Chipfilter anwenden
  if (selectedUserFilters.value.includes("super_admin")) {
    result = result.filter((user) => user.super_admin);
  }

  if (selectedUserFilters.value.includes("developer")) {
    result = result.filter((user) => user.developer);
  }

  if (selectedUserFilters.value.includes("admin")) {
    result = result.filter((user) => user.admin);
  }

  if (selectedUserFilters.value.includes("active")) {
    result = result.filter((user) => user.aktiv);
  }

  if (selectedUserFilters.value.includes("lagerist")) {
    result = result.filter((user) => user.lagerist);
  }

  if (selectedUserFilters.value.includes("monteur")) {
    result = result.filter((user) => user.monteur);
  }

  if (selectedUserFilters.value.includes("office")) {
    result = result.filter((user) => user.office);
  }

  if (selectedUserFilters.value.includes("refurbish")) {
    result = result.filter((user) => user.refurbish);
  }

  if (selectedUserFilters.value.includes("dtc")) {
    result = result.filter((user) => user.dtc);
  }

  if (selectedUserFilters.value.includes("inventur")) {
    result = result.filter((user) => user.inventur);
  }

  return result;
});

// Für den Registrierungsdialog
const newUserPasswordMinLength = computed(
  () => newUser.value.passwort.length >= 8
);
const newUserPasswordUpperCase = computed(() =>
  /[A-Z]/.test(newUser.value.passwort)
);
const newUserPasswordLowerCase = computed(() =>
  /[a-z]/.test(newUser.value.passwort)
);
const newUserPasswordSpecialChar = computed(() =>
  /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(newUser.value.passwort)
);
const newUserPasswordsMatch = computed(
  () =>
    newUser.value.passwortRepeat.length > 0 &&
    newUser.value.passwort === newUser.value.passwortRepeat
);
const newUserPasswordValid = computed(
  () =>
    newUserPasswordMinLength.value &&
    newUserPasswordUpperCase.value &&
    newUserPasswordLowerCase.value &&
    newUserPasswordSpecialChar.value &&
    newUserPasswordsMatch.value
);

// Sessions-Verwaltung
const sessionsDialog = ref(false);
const selectedUser = ref(null);
const userSessions = ref([]);
const loadingSessions = ref(false);

// Session-Dialog öffnen und Daten laden
const showUserSessions = async (user) => {
  selectedUser.value = user;
  sessionsDialog.value = true;
  await fetchUserSessions(user.id);
};

// Benutzersessions laden
const fetchUserSessions = async (userId) => {
  loadingSessions.value = true;
  try {
    const response = await axios.get(
      `${API_BASE_URL}admin/user_sessions.php?action=get&user_id=${userId}`,
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      userSessions.value = response.data.sessions;
    } else {
      toast.error("Fehler beim Laden der Sessions");
    }
  } catch (error) {
    toast.error("Netzwerkfehler beim Laden der Sessions");
  } finally {
    loadingSessions.value = false;
  }
};

// Funktion im Frontend für die Session-Beendigung
// Funktion im Frontend für die Session-Beendigung
const terminateSession = async (sessionId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}admin/user_sessions.php`,
      {
        action: "terminate",
        session_id: sessionId,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      // Prüfen, ob die eigene Session beendet wurde
      const currentUserId = authStore.user?.id;
      const terminatedSessionData = userSessions.value.find(
        (s) => s.id === sessionId
      );
      const didTerminateOwnSession =
        terminatedSessionData?.user_id === currentUserId;

      // Aktualisiere die Nutzerliste
      await fetchUserSessions(selectedUser.value.id);

      // Prüfe, ob die eigene Session beendet wurde
      if (didTerminateOwnSession) {
        toast.info(
          "Deine eigene Session wurde beendet. Du wirst in 3 Sekunden ausgeloggt."
        );

        // Dem Benutzer Zeit geben, die Nachricht zu lesen
        setTimeout(() => {
          // Lokalen Auth-Status zurücksetzen und ausloggen
          authStore.logout();
        }, 3000);
      }
    } else {
      toast.error(response.data.message || "Fehler beim Beenden der Session");
    }
  } catch (error) {
    toast.error("Netzwerkfehler beim Beenden der Session");
  }
};

// Alle Sessions eines Benutzers beenden
const terminateAllUserSessions = async () => {
  if (!selectedUser.value) return;

  const isTerminatingSelf = selectedUser.value.id === authStore.user?.id;

  try {
    const response = await axios.post(
      `${API_BASE_URL}admin/user_sessions.php`,
      {
        action: "terminate_all",
        user_id: selectedUser.value.id,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Alle Sessions des Benutzers wurden beendet");

      // Aktualisiere die Nutzerliste
      await fetchUserSessions(selectedUser.value.id);

      // Wenn der Admin seine eigenen Sessions beendet hat
      if (isTerminatingSelf) {
        toast.info(
          "Deine eigenen Sessions wurden beendet. Du wirst in 3 Sekunden ausgeloggt."
        );

        // Dem Benutzer Zeit geben, die Nachricht zu lesen
        setTimeout(() => {
          // Lokalen Auth-Status zurücksetzen und ausloggen
          authStore.logout();
        }, 3000);
      } else {
        // Nach kurzer Verzögerung Dialog schließen und Nutzerliste aktualisieren
        setTimeout(() => {
          sessionsDialog.value = false;
          fetchUsers(); // Nutzerliste aktualisieren
        }, 1500);
      }
    } else {
      toast.error(
        response.data.message || "Fehler beim Beenden aller Sessions"
      );
    }
  } catch (error) {
    toast.error("Netzwerkfehler beim Beenden aller Sessions");
  }
};

// Hilfsfunktionen für die Anzeige
const formatDeviceInfo = (deviceInfo) => {
  if (!deviceInfo) return "Unbekanntes Gerät";

  // Browser und Gerät extrahieren
  const browserMatches = deviceInfo.match(
    /(Chrome|Firefox|Safari|Edge|MSIE|Opera)\/[\d.]+/
  );
  const deviceMatches = deviceInfo.match(
    /(iPhone|iPad|Android|Windows|Macintosh|Linux)/
  );

  const browser = browserMatches
    ? browserMatches[0].split("/")[0]
    : "Unbekannter Browser";
  const device = deviceMatches ? deviceMatches[1] : "Unbekanntes Gerät";

  return `${browser} auf ${device}`;
};

const getDeviceIcon = (deviceInfo) => {
  if (!deviceInfo) return "mdi-desktop-classic";

  if (
    deviceInfo.includes("Android") ||
    deviceInfo.includes("iPhone") ||
    deviceInfo.includes("iPad")
  ) {
    return "mdi-cellphone";
  } else if (deviceInfo.includes("Windows")) {
    return "mdi-microsoft-windows";
  } else if (deviceInfo.includes("Macintosh")) {
    return "mdi-apple";
  } else if (deviceInfo.includes("Linux")) {
    return "mdi-linux";
  } else {
    return "mdi-desktop-classic";
  }
};

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return "-";

  const date = new Date(dateTimeStr);
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Status-Farbe und -Text wie in anderen Komponenten
const getStatusColor = (status) => {
  switch (status) {
    case "online":
      return "success";
    case "away":
      return "amber";
    case "busy":
      return "error";
    case "mobile":
      return "purple";
    case "custom":
      return "info";
    case "offline":
    default:
      return "grey";
  }
};

const getStatusText = (status) => {
  switch (status) {
    case "online":
      return "Online";
    case "away":
      return "Abwesend";
    case "busy":
      return "Beschäftigt";
    case "mobile":
      return "Mobil";
    case "custom":
      return "Benutzerdefiniert";
    case "offline":
    default:
      return "Offline";
  }
};

const toggleDeveloperStatus = async (user) => {
  try {
    // Prüfen, ob der aktuelle Nutzer Super Admin ist
    if (!authStore.user?.super_admin && !authStore.user?.developer) {
      toast.error(
        "Nur Super Admins oder Developer können den Developer-Status ändern!"
      );
      return;
    }

    const newStatus = user.developer ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_developer",
        user_id: user.id,
        developer: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.developer = !user.developer; // Direkte Umkehrung des booleschen Wertes
      toast.success(
        `Benutzer ist jetzt ${user.developer ? "Developer" : "kein Developer"}!`
      );
    } else {
      toast.error(
        response.data.message || "Fehler beim Ändern des Developer-Status!"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Developer-Status!");
  }
};

// Super Admin-Status umschalten
const toggleSuperAdminStatus = async (user) => {
  try {
    // Prüfen, ob der aktuelle Nutzer Super Admin ist
    if (!authStore.user?.super_admin) {
      toast.error("Nur Super Admins können den Super Admin-Status ändern!");
      return;
    }

    const newStatus = user.super_admin ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_super_admin",
        user_id: user.id,
        super_admin: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.super_admin = !user.super_admin; // Direkte Umkehrung des booleschen Wertes
      toast.success(
        `Benutzer ist jetzt ${
          user.super_admin ? "Super Admin" : "kein Super Admin"
        }!`
      );
    } else {
      toast.error(
        response.data.message || "Fehler beim Ändern des Super Admin-Status!"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Super Admin-Status!");
  }
};

// Toggle Preissichtbarkeit
const togglePriceVisibility = () => {
  pricesVisible.value = !pricesVisible.value;
};

// Für den Bearbeitungsdialog (optional, nur wenn ein Passwort gesetzt wird)
const editPasswordMinLength = computed(() => newPassword.value.length >= 8);
const editPasswordUpperCase = computed(() => /[A-Z]/.test(newPassword.value));
const editPasswordLowerCase = computed(() => /[a-z]/.test(newPassword.value));
const editPasswordSpecialChar = computed(() =>
  /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(newPassword.value)
);
const editPasswordsMatch = computed(
  () =>
    confirmPassword.value.length > 0 &&
    newPassword.value === confirmPassword.value
);
const editPasswordValid = computed(
  () =>
    editPasswordMinLength.value &&
    editPasswordUpperCase.value &&
    editPasswordLowerCase.value &&
    editPasswordSpecialChar.value &&
    editPasswordsMatch.value
);

// ======== Kategorienverwaltung ========
const categories = ref([]);
const categoryDialog = ref(false);
const categoryEditMode = ref(false);
const categoryItem = ref({ name: "", typ: "", preis: 0 });
const deleteCategoryDialog = ref(false);
const deleteCategoryId = ref(null);

// ======== Preisverwaltung ========
const preise = ref([]);
const geraetetypen = ref([]);
const kategorien = ref([]);
const preisDialog = ref(false);
const preisEditMode = ref(false);
const preisItem = ref({ geraetetyp_id: null, kategorie_id: null, preis: 0 });
const pricesVisible = ref(false);

// ======== Durchwahlen ========
const durchwahlen = ref([]);
const durchwahlSearch = ref("");
const durchwahlDialog = ref(false);
const durchwahlEditMode = ref(false);
const durchwahlItem = ref({
  name: "",
  vorname: "",
  durchwahl: "",
  abteilung: "",
});
const deleteDurchwahlDialog = ref(false);
const deleteDurchwahlId = ref(null);

// Gefilterte Durchwahlen basierend auf Suche
const filteredDurchwahlen = computed(() => {
  if (!durchwahlSearch.value) return durchwahlen.value;

  const searchTerm = durchwahlSearch.value.toLowerCase();
  return durchwahlen.value.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm) ||
      item.vorname.toLowerCase().includes(searchTerm) ||
      item.durchwahl.toLowerCase().includes(searchTerm) ||
      (item.abteilung && item.abteilung.toLowerCase().includes(searchTerm))
  );
});

// In AdminView.vue, beim Script-Teil
const cleaningTokens = ref(false);

const cleanupExpiredTokens = async () => {
  cleaningTokens.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}admin/cron_cleanup_tokens.php`,
      { action: "cleanup" },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success(
        `${response.data.deleted_tokens} abgelaufene Tokens wurden gelöscht.`
      );
    } else {
      toast.error(response.data.message || "Fehler beim Löschen der Tokens.");
    }
  } catch (error) {
    toast.error("Fehler beim Löschen abgelaufener Tokens");
  } finally {
    cleaningTokens.value = false;
  }
};

// ======== Kurztexte ========
const kurztexte = ref([]);
const kurztextDialog = ref(false);
const kurztextEditMode = ref(false);
const kurztextItem = ref({
  id: null,
  nummer: 0,
  bezeichnung: "",
  kurztext: "",
});
const deleteKurztextDialog = ref(false);
const deleteKurztextId = ref(null);

// ======== Gesperrte Benutzer ========
const lockedUsers = ref([]);
const loadingLockedUsers = ref(false);
const unlockDialog = ref(false);
const unlockItem = ref(null);
const historyDialog = ref(false);
const historyItem = ref(null);
const loginHistory = ref([]);
const loadingHistory = ref(false);

// ======== Login-Versuche ========
const loginAttempts = ref([]);
const loginAttemptsStats = ref({
  total_attempts: 0,
  successful_attempts: 0,
  failed_attempts: 0,
  unique_users: 0,
  unique_ips: 0
});
const loadingLoginAttempts = ref(false);
const loginAttemptsFilter = ref({
  username: '',
  ip_address: '',
  success: null,
  days: 30
});
const loginAttemptsDetailDialog = ref(false);
const selectedLoginAttempt = ref(null);
const loginAttemptsPagination = ref({
  page: 1,
  itemsPerPage: 50,
  totalItems: 0
});

// ======== Announcements ========
const announcementTab = ref("editor");
const announcementMessage = ref({
  message: "",
  type: "announcement",
});

// WebSocket für Announcements
let wsAnnouncement = null;
const initWebSocket = () => {
  if (wsAnnouncement) {
    wsAnnouncement.close();
  }

  wsAnnouncement = new WebSocket(`${WS_URL}?type=announcement`);

  wsAnnouncement.onopen = () => {
  };

  wsAnnouncement.onerror = (error) => {
  };

  // Ping zur Verbindungserhaltung
  const pingInterval = setInterval(() => {
    if (wsAnnouncement && wsAnnouncement.readyState === WebSocket.OPEN) {
      wsAnnouncement.send("ping");
    }
  }, 30000);

  // Cleanup bei Komponentenabbau
  onBeforeUnmount(() => {
    clearInterval(pingInterval);
    if (wsAnnouncement) {
      wsAnnouncement.close();
      wsAnnouncement = null;
    }
  });
};

// ======== Nutzerverwaltung - Funktionen ========

// Dialog zum Registrieren öffnen
const openRegisterDialog = () => {
  newUser.value = {
    loginname: "",
    vorname: "",
    nachname: "",
    email: "",
    mitarbeiternummer: "",
    abteilung: "Support",
    passwort: "",
    passwortRepeat: "",
    birthday: "",
    birthday_public: false,
  };
  registerDialog.value = true;
};

const sendPasswordResetLink = async (user) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}admin/send_password_reset.php`,
      { action: "send", user_id: user.id },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    if (response.data.status === "success") {
      toast.success(`Passwort-Reset-Link wurde an ${user.email} gesendet!`);
    } else {
      toast.error(
        response.data.message || "Fehler beim Senden des Reset-Links"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Senden des Reset-Links");
  }
};

// Nutzer registrieren
const registerUser = async () => {
  if (
    !newUser.value.loginname ||
    !newUser.value.vorname ||
    !newUser.value.nachname ||
    !newUser.value.email ||
    !newUser.value.mitarbeiternummer ||
    !newUser.value.passwort ||
    !newUser.value.passwortRepeat ||
    !newUser.value.abteilung
  ) {
    toast.error("Bitte alle Felder ausfüllen!");
    return;
  }

  if (newUser.value.passwort.length < 8) {
    toast.error("Das Passwort muss mindestens 8 Zeichen lang sein!");
    return;
  }

  if (newUser.value.passwort !== newUser.value.passwortRepeat) {
    toast.error("Die Passwörter stimmen nicht überein!");
    return;
  }

  try {
    const response = await axios.post(
      `${API_BASE_URL}register.php`,
      {
        action: "register",
        loginname: newUser.value.loginname,
        vorname: newUser.value.vorname,
        nachname: newUser.value.nachname,
        email: newUser.value.email,
        mitarbeiternummer: newUser.value.mitarbeiternummer,
        abteilung: newUser.value.abteilung,
        passwort: newUser.value.passwort,
        birthday: newUser.value.birthday,
        birthday_public: newUser.value.birthday_public,
        is_shared_account: newUser.value.is_shared_account ? 1 : 0,
      },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      }
    );

    if (response.data.status === "success") {
      toast.success("Nutzer erfolgreich registriert!");
      registerDialog.value = false;
      fetchUsers();
    } else {
      toast.error(response.data.message || "Fehler bei der Registrierung!");
    }
  } catch (error) {
    const apiMessage =
      error?.response?.data?.message ||
      "Serverfehler! Bitte versuchen Sie es später erneut.";
    toast.error(apiMessage);
  }
};

const normalizeBirthdayForInput = (value) => {
  if (!value || value === "0000-00-00") {
    return "";
  }

  // Manche APIs liefern ein ISO-Datum mit Uhrzeit; Date-Inputs erwarten nur YYYY-MM-DD.
  if (typeof value === "string" && value.length >= 10) {
    const candidate = value.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
      return candidate;
    }
  }

  return "";
};

const normalizeBirthdayForRequest = (value) => {
  const normalized = normalizeBirthdayForInput(value);
  return normalized || null;
};

// Nutzer laden
const fetchUsers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/users.php?action=get`, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.status === "success") {
      users.value = response.data.users.map((user) => ({
        ...user,
        // Robustere Umwandlung in boolesche Werte
        aktiv: user.aktiv === "1" || user.aktiv === 1 || user.aktiv === true,
        admin: user.admin === "1" || user.admin === 1 || user.admin === true,
        super_admin:
          user.super_admin === "1" ||
          user.super_admin === 1 ||
          user.super_admin === true,
        developer:
          user.developer === "1" ||
          user.developer === 1 ||
          user.developer === true,
        lagerist:
          user.lagerist === "1" ||
          user.lagerist === 1 ||
          user.lagerist === true,
        monteur:
          user.monteur === "1" || user.monteur === 1 || user.monteur === true,
        office:
          user.office === "1" || user.office === 1 || user.office === true,
        refurbish:
          user.refurbish === "1" || user.refurbish === 1 || user.refurbish === true,
        dtc:
          user.dtc === "1" || user.dtc === 1 || user.dtc === true,
        inventur:
          user.inventur === "1" || user.inventur === 1 || user.inventur === true,
        is_shared_account:
          user.is_shared_account === "1" ||
          user.is_shared_account === 1 ||
          user.is_shared_account === true,
        birthday: normalizeBirthdayForInput(user.birthday),
      }));
    } else {
      toast.error("Fehler beim Laden der Nutzer!");
    }
  } catch (error) {
    toast.error("Netzwerkfehler beim Laden der Nutzer!");
  }
};

// Admin-Status umschalten
const toggleAdminStatus = async (user) => {
  try {
    const newStatus = user.admin ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_admin",
        user_id: user.id,
        admin: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.admin = !user.admin; // Direkte Umkehrung des booleschen Wertes
      toast.success(
        `Benutzer ist jetzt ${user.admin ? "Admin" : "kein Admin"}!`
      );
    } else {
      toast.error(
        response.data.message || "Fehler beim Ändern des Adminstatus!"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Adminstatus!");
  }
};

// Lagerist-Status umschalten
const toggleLageristStatus = async (user) => {
  try {
    const newStatus = user.lagerist ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_lagerist",
        user_id: user.id,
        lagerist: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.lagerist = !user.lagerist; // Direkte Umkehrung des booleschen Wertes
      toast.success(
        `Benutzer ist jetzt ${user.lagerist ? "Lagerist" : "kein Lagerist"}!`
      );
    } else {
      toast.error(
        response.data.message || "Fehler beim Ändern des Lagerist-Status!"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Lagerist-Status!");
  }
};

const toggleMonteurStatus = async (user) => {
  try {
    const newStatus = user.monteur ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_monteur",
        user_id: user.id,
        monteur: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.monteur = !user.monteur; // Direkte Umkehrung des booleschen Wertes
      toast.success(
        `Benutzer ist jetzt ${user.monteur ? "Monteur" : "kein Monteur"}!`
      );
    } else {
      toast.error(
        response.data.message || "Fehler beim Ändern des Monteur-Status!"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Monteur-Status!");
  }
};

// Office-Status umschalten
const toggleOfficeStatus = async (user) => {
  try {
    const newStatus = user.office ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_office",
        user_id: user.id,
        office: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.office = !user.office;
      toast.success(
        `Benutzer ist jetzt ${user.office ? "Office-Mitglied" : "kein Office-Mitglied"}!`
      );
    } else {
      toast.error(
        response.data.message || "Fehler beim Ändern des Office-Status!"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Office-Status!");
  }
};

// Refurbish-Status umschalten
const toggleRefurbishStatus = async (user) => {
  try {
    const newStatus = user.refurbish ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_refurbish",
        user_id: user.id,
        refurbish: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.refurbish = !user.refurbish;
      toast.success(
        `Benutzer ist jetzt ${user.refurbish ? "Refurbish-Mitglied" : "kein Refurbish-Mitglied"}!`
      );
    } else {
      toast.error(
        response.data.message || "Fehler beim Ändern des Refurbish-Status!"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Refurbish-Status!");
  }
};

// DTC-Status umschalten
const toggleDtcStatus = async (user) => {
  try {
    const newStatus = user.dtc ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_dtc",
        user_id: user.id,
        dtc: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.dtc = !user.dtc;
      toast.success(
        `Benutzer ist jetzt ${user.dtc ? "DTC-Mitglied" : "kein DTC-Mitglied"}!`
      );
    } else {
      toast.error(
        response.data.message || "Fehler beim ändern des DTC-Status!"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des DTC-Status!");
  }
};

// Inventur-Status umschalten
const toggleInventurStatus = async (user) => {
  try {
    const newStatus = user.inventur ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_inventur",
        user_id: user.id,
        inventur: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.inventur = !user.inventur;
      toast.success(
        `Benutzer ist jetzt ${user.inventur ? "Inventur-Mitglied" : "kein Inventur-Mitglied"}!`
      );
    } else {
      toast.error(
        response.data.message || "Fehler beim Ändern des Inventur-Status!"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Inventur-Status!");
  }
};

const toggleUserStatus = async (user) => {
  try {
    const newStatus = user.aktiv ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_status",
        user_id: user.id,
        aktiviert: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.aktiv = !user.aktiv; // Direkte Umkehrung des booleschen Wertes
      toast.success(
        `Benutzer wurde ${user.aktiv ? "aktiviert" : "deaktiviert"}!`
      );
    } else {
      toast.error(response.data.message || "Fehler beim Ändern des Status!");
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Status!");
  }
};

// Bearbeitungsdialog öffnen
const openEditDialog = (user) => {
  editUser.value = {
    ...user,
    birthday: normalizeBirthdayForInput(user.birthday),
  };
  newPassword.value = "";
  confirmPassword.value = "";
  editDialog.value = true;
};

// Nutzeränderungen speichern
const saveUserChanges = async () => {
  if (newPassword.value !== confirmPassword.value && newPassword.value !== "") {
    toast.error("Passwörter stimmen nicht überein!");
    return;
  }

  try {
    const requestData = {
      action: "update",
      ...editUser.value,
      birthday: normalizeBirthdayForRequest(editUser.value.birthday),
      newPassword: newPassword.value,
      password_only: newPassword.value !== "",
    };

    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      requestData,
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    if (response.data.status === "success") {
      toast.success("Nutzer erfolgreich aktualisiert!");
      editDialog.value = false;
      fetchUsers();
    } else {
      toast.error(
        response.data.message || "Fehler beim Aktualisieren des Nutzers"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Aktualisieren des Nutzers!");
  }
};

// ======== Nutzerverwaltung - Funktionen (Fortsetzung) ========

// Nutzer löschen
const confirmDeleteUser = (user) => {
  deleteUserId.value = user.id;
  deleteDialog.value = true;
};

const deleteUser = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "delete",
        user_id: deleteUserId.value,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success("Nutzer erfolgreich gelöscht!");
      fetchUsers();
    } else {
      toast.error(response.data.message || "Fehler beim Löschen des Nutzers");
    }
  } catch (error) {
    toast.error("Fehler beim Löschen des Nutzers!");
  } finally {
    deleteDialog.value = false;
  }
};

// ======== Kategorienverwaltung - Funktionen ========

// Kategorie-Farben basierend auf Typ
const getCategoryTypeColor = (typ) => {
  switch (typ) {
    case "Gerätetyp":
      return "success";
    case "Reparaturtyp":
      return "primary";
    case "Retourentyp":
      return "amber-darken-2";
    default:
      return "grey";
  }
};

// Kategorien laden
const fetchCategories = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/categories.php?action=get`, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.status === "success") {
      categories.value = [
        ...(response.data.geraetetypen || []),
        ...(response.data.reparaturtypen || []),
        ...(response.data.retourentypen || []),
      ].sort((a, b) => a.order_id - b.order_id);
    } else {
      toast.error("Fehler beim Laden der Kategorien!");
    }
  } catch (error) {
    toast.error("Fehler beim Laden der Kategorien!");
  }
};

// Kategorie-Dialog öffnen (Hinzufügen)
const openAddCategoryDialog = () => {
  categoryEditMode.value = false;
  categoryItem.value = { name: "", typ: "Gerätetyp" };
  categoryDialog.value = true;
};

// Kategorie-Dialog öffnen (Bearbeiten)
const openEditCategoryDialog = (item) => {
  categoryEditMode.value = true;
  categoryItem.value = { ...item };
  categoryDialog.value = true;
};

// Kategorie speichern
const saveCategory = async () => {
  if (!categoryItem.value.name || !categoryItem.value.typ) {
    toast.error("Bitte Name und Typ angeben!");
    return;
  }

  try {
    const action = categoryEditMode.value ? "update" : "add";
    const response = await axios.post(
      `${API_BASE_URL}admin/categories.php`,
      {
        action,
        ...categoryItem.value,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success(
        `Kategorie erfolgreich ${
          categoryEditMode.value ? "aktualisiert" : "hinzugefügt"
        }!`
      );
      fetchCategories();
      fetchTypen(); // Typen aktualisieren für Preise
      categoryDialog.value = false;
    } else {
      toast.error(
        response.data.message || "Fehler beim Speichern der Kategorie"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Speichern der Kategorie!");
  }
};

// Löschen einer Kategorie bestätigen
const confirmDeleteCategory = (id) => {
  deleteCategoryId.value = id;
  deleteCategoryDialog.value = true;
};

// Kategorie löschen
const deleteCategory = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}admin/categories.php`,
      {
        action: "delete",
        id: deleteCategoryId.value,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success("Kategorie erfolgreich gelöscht!");
      fetchCategories();
      fetchTypen(); // Typen aktualisieren für Preise
    } else {
      toast.error(response.data.message || "Fehler beim Löschen der Kategorie");
    }
  } catch (error) {
    toast.error("Fehler beim Löschen der Kategorie!");
  } finally {
    deleteCategoryDialog.value = false;
  }
};

// Reihenfolge der Kategorien aktualisieren
const updateCategoryOrder = async () => {
  try {
    // Reihenfolge aktualisieren
    categories.value.forEach((item, index) => {
      item.order_id = index + 1;
    });

    const response = await axios.post(
      `${API_BASE_URL}admin/categories.php`,
      {
        action: "update_order",
        categories: categories.value,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success("Reihenfolge erfolgreich aktualisiert!");
    } else {
      toast.error("Fehler beim Aktualisieren der Reihenfolge");
    }
  } catch (error) {
    toast.error("Fehler beim Speichern der Reihenfolge!");
  }
};

// ======== Preisverwaltung - Funktionen ========

// Preise laden
const fetchPreise = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/prices.php?action=get`, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.status === "success") {
      preise.value = response.data.preise || [];
    } else {
      toast.error("Fehler beim Laden der Preise!");
    }
  } catch (error) {
    toast.error("Fehler beim Laden der Preise!");
  }
};

// Typen für Preisverwaltung laden
const fetchTypen = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/categories.php?action=get`, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.status === "success") {
      geraetetypen.value = response.data.geraetetypen || [];
      kategorien.value = [
        ...(response.data.reparaturtypen || []),
        ...(response.data.retourentypen || []),
      ];
    } else {
      toast.error("Fehler beim Laden der Kategorietypen!");
    }
  } catch (error) {
    toast.error("Fehler beim Laden der Kategorietypen!");
  }
};

// Preisdialog öffnen (Hinzufügen)
const openAddPreisDialog = () => {
  preisEditMode.value = false;
  preisItem.value = { geraetetyp_id: null, kategorie_id: null, preis: 0 };
  preisDialog.value = true;
};

// Preisdialog öffnen (Bearbeiten)
const openEditPreisDialog = (item) => {
  preisEditMode.value = true;

  // IDs für Gerätetyp & Kategorie anhand der Namen finden
  const selectedGeraetetyp = geraetetypen.value.find(
    (g) => g.name === item.geraetetyp
  );

  const selectedKategorie = kategorien.value.find(
    (k) => k.name === item.kategorie
  );

  preisItem.value = {
    id: item.id,
    geraetetyp_id: selectedGeraetetyp ? selectedGeraetetyp.id : null,
    kategorie_id: selectedKategorie ? selectedKategorie.id : null,
    preis: parseFloat(item.preis),
  };

  preisDialog.value = true;
};

// Preis speichern
const savePreis = async () => {
  if (
    !preisItem.value.geraetetyp_id ||
    !preisItem.value.kategorie_id ||
    preisItem.value.preis === null
  ) {
    toast.error("Bitte alle Felder ausfüllen!");
    return;
  }

  try {
    const action = preisEditMode.value ? "update" : "add";
    const response = await axios.post(
      `${API_BASE_URL}admin/prices.php`,
      {
        action,
        ...preisItem.value,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success(
        `Preis erfolgreich ${
          preisEditMode.value ? "aktualisiert" : "hinzugefügt"
        }!`
      );
      fetchPreise();
      preisDialog.value = false;
    } else {
      toast.error(response.data.message || "Fehler beim Speichern des Preises");
    }
  } catch (error) {
    toast.error("Fehler beim Speichern des Preises!");
  }
};

// Preis löschen
const confirmDeletePreis = async (id) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}admin/prices.php`,
      {
        action: "delete",
        id: id,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success("Preis erfolgreich gelöscht!");
      fetchPreise();
    } else {
      toast.error(response.data.message || "Fehler beim Löschen des Preises");
    }
  } catch (error) {
    toast.error("Fehler beim Löschen des Preises!");
  }
};

// ======== Login-Versuche - Funktionen ========

// Table Headers
const loginAttemptsHeaders = [
  { title: 'Status', key: 'success', sortable: true, align: 'center' },
  { title: 'Username', key: 'username', sortable: true },
  { title: 'IP-Adresse', key: 'ip_address', sortable: true },
  { title: 'Zeitstempel', key: 'created_at', sortable: true },
  { title: 'Gesperrt bis', key: 'locked_until', sortable: true },
  { title: 'Aktionen', key: 'actions', sortable: false, align: 'center' }
];

// Login-Versuche laden
const fetchLoginAttempts = async () => {
  loadingLoginAttempts.value = true;
  try {
    const params = new URLSearchParams();

    if (loginAttemptsFilter.value.username) {
      params.append('username', loginAttemptsFilter.value.username);
    }
    if (loginAttemptsFilter.value.ip_address) {
      params.append('ip_address', loginAttemptsFilter.value.ip_address);
    }
    if (loginAttemptsFilter.value.success !== null && loginAttemptsFilter.value.success !== undefined) {
      params.append('success', String(loginAttemptsFilter.value.success));
    }
    params.append('days', String(loginAttemptsFilter.value.days));
    params.append('limit', String(loginAttemptsPagination.value.itemsPerPage));
    params.append('offset', String((loginAttemptsPagination.value.page - 1) * loginAttemptsPagination.value.itemsPerPage));
    params.append('action', 'get');

    const response = await axios.get(`${API_BASE_URL}loginAttempts.php?${params.toString()}`, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      loginAttempts.value = response.data.attempts;
      loginAttemptsStats.value = response.data.stats;
      loginAttemptsPagination.value.totalItems = response.data.stats.total_attempts;
    } else {
      toast.error(response.data.message || "Fehler beim Laden der Login-Versuche");
    }
  } catch (error) {
    toast.error("Fehler beim Laden der Login-Versuche");
  } finally {
    loadingLoginAttempts.value = false;
  }
};

// Filter zurücksetzen
const clearLoginAttemptsFilter = () => {
  loginAttemptsFilter.value = {
    username: '',
    ip_address: '',
    success: null,
    days: 30
  };
  fetchLoginAttempts();
};

// Details anzeigen
const showAttemptDetails = (attempt) => {
  selectedLoginAttempt.value = attempt;
  loginAttemptsDetailDialog.value = true;
};

// Einzelnen Versuch löschen
const deleteLoginAttempt = async (attemptId) => {
  if (!confirm('Möchten Sie diesen Eintrag wirklich löschen?')) return;

  try {
    const response = await axios.delete(`${API_BASE_URL}loginAttempts.php`, {
      withCredentials: true,
      data: { action: "delete", attempt_id: attemptId }
    });

    if (response.data.status === 'success') {
      toast.success('Eintrag gelöscht');
      fetchLoginAttempts();
    } else {
      toast.error(response.data.message || 'Fehler beim Löschen');
    }
  } catch (error) {
    toast.error('Fehler beim Löschen des Eintrags');
  }
};

// Alte Einträge löschen
const confirmDeleteOldAttempts = async () => {
  const days = prompt('Einträge löschen, die älter sind als (Tage):', '90');
  if (!days) return;

  try {
    const response = await axios.delete(`${API_BASE_URL}loginAttempts.php`, {
      withCredentials: true,
      data: { action: "delete", older_than_days: parseInt(days) }
    });

    if (response.data.status === 'success') {
      toast.success(`${response.data.deleted_count} Einträge gelöscht`);
      fetchLoginAttempts();
    } else {
      toast.error(response.data.message || 'Fehler beim Löschen');
    }
  } catch (error) {
    toast.error('Fehler beim Löschen der alten Einträge');
  }
};

// Hilfsfunktionen
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const isLockActive = (lockedUntil) => {
  if (!lockedUntil) return false;
  return new Date(lockedUntil) > new Date();
};

// ======== Durchwahlen - Funktionen ========

// Durchwahlen laden
const fetchDurchwahlen = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}extensions.php?action=get`, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.status === "success") {
      durchwahlen.value = response.data.durchwahlen || [];
    } else {
      toast.error("Fehler beim Laden der Durchwahlen!");
    }
  } catch (error) {
    toast.error("Fehler beim Laden der Durchwahlen!");
  }
};

// Durchwahlendialog öffnen (Hinzufügen)
const openAddDurchwahlDialog = () => {
  durchwahlEditMode.value = false;
  durchwahlItem.value = { name: "", vorname: "", durchwahl: "", abteilung: "" };
  durchwahlDialog.value = true;
};

// Durchwahlendialog öffnen (Bearbeiten)
const openEditDurchwahlDialog = (item) => {
  durchwahlEditMode.value = true;
  durchwahlItem.value = { ...item };
  durchwahlDialog.value = true;
};

// Durchwahl speichern
const saveDurchwahl = async () => {
  if (
    !durchwahlItem.value.name ||
    !durchwahlItem.value.vorname ||
    !durchwahlItem.value.durchwahl
  ) {
    toast.error("Bitte alle Pflichtfelder ausfüllen!");
    return;
  }

  try {
    const action = durchwahlEditMode.value ? "update" : "add";
    const response = await axios.post(
      `${API_BASE_URL}extensions.php`,
      {
        ...durchwahlItem.value,
        action,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success(
        `Durchwahl erfolgreich ${
          durchwahlEditMode.value ? "aktualisiert" : "hinzugefügt"
        }!`
      );
      fetchDurchwahlen();
      durchwahlDialog.value = false;
    } else {
      toast.error(
        response.data.message || "Fehler beim Speichern der Durchwahl"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Speichern der Durchwahl!");
  }
};

// Löschen einer Durchwahl bestätigen
const confirmDeleteDurchwahl = (id) => {
  deleteDurchwahlId.value = id;
  deleteDurchwahlDialog.value = true;
};

// Durchwahl löschen
const deleteDurchwahl = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}extensions.php`,
      {
        action: "delete",
        id: deleteDurchwahlId.value,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success("Durchwahl erfolgreich gelöscht!");
      fetchDurchwahlen();
    } else {
      toast.error(response.data.message || "Fehler beim Löschen der Durchwahl");
    }
  } catch (error) {
    toast.error("Fehler beim Löschen der Durchwahl!");
  } finally {
    deleteDurchwahlDialog.value = false;
  }
};

// ======== Kurztexte - Funktionen ========

// Kurztexte laden
const fetchKurztexte = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}textsnippets.php`, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.status === "success") {
      kurztexte.value = response.data.kurztexte || [];
    } else {
      toast.error("Fehler beim Laden der Kurztexte!");
    }
  } catch (error) {
    toast.error("Fehler beim Laden der Kurztexte!");
  }
};

// Dialog zum Hinzufügen öffnen
const openAddKurztextDialog = () => {
  kurztextEditMode.value = false;
  kurztextItem.value = { nummer: "", bezeichnung: "", kurztext: "" };
  kurztextDialog.value = true;
};

// Kurztextdialog öffnen (Bearbeiten)
const openEditKurztextDialog = (item) => {
  kurztextEditMode.value = true;
  kurztextItem.value = { ...item };
  kurztextDialog.value = true;
};

// Kurztext speichern
const saveKurztext = async () => {
  if (!kurztextItem.value.nummer || !kurztextItem.value.kurztext) {
    toast.error("Bitte alle Felder ausfüllen!");
    return;
  }

  try {
    const action = kurztextEditMode.value ? "update" : "add";
    const response = await axios.post(
      `${API_BASE_URL}textsnippets.php`,
      {
        ...kurztextItem.value,
        action,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success(
        `Kurztext erfolgreich ${
          kurztextEditMode.value ? "aktualisiert" : "hinzugefügt"
        }!`
      );
      fetchKurztexte();
      kurztextDialog.value = false;
    } else {
      toast.error(
        response.data.message || "Fehler beim Speichern des Kurztextes"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Speichern des Kurztextes!");
  }
};

// Löschen eines Kurztextes bestätigen
const confirmDeleteKurztext = (id) => {
  deleteKurztextId.value = id;
  deleteKurztextDialog.value = true;
};

// Kurztext löschen
const deleteKurztext = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}textsnippets.php`,
      {
        action: "delete",
        id: deleteKurztextId.value,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success("Kurztext erfolgreich gelöscht!");
      fetchKurztexte();
    } else {
      toast.error(
        response.data.message || "Fehler beim Löschen des Kurztextes"
      );
    }
  } catch (error) {
    toast.error("Fehler beim Löschen des Kurztextes!");
  } finally {
    deleteKurztextDialog.value = false;
  }
};

// ======== Gesperrte Benutzer - Funktionen ========

// Gesperrte Benutzer laden
const fetchLockedUsers = async () => {
  loadingLockedUsers.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}admin/locked_users.php?action=get_locked_users`, {
      withCredentials: true,
    });

    if (response.data.status === "success") {
      lockedUsers.value = response.data.locked_users;
    } else {
      toast.error(response.data.message || "Fehler beim Laden der gesperrten Benutzer");
    }
  } catch (error) {
    if (error.response) {
      if (error.response.data?.debug) {
        toast.error(`Datenbankfehler: ${error.response.data.debug.message}`);
      } else {
        toast.error(error.response.data?.message || "Fehler beim Laden der gesperrten Benutzer");
      }
    } else {
      toast.error("Netzwerkfehler beim Laden der gesperrten Benutzer");
    }
  } finally {
    loadingLockedUsers.value = false;
  }
};

// Dialog zum Entsperren öffnen
const confirmUnlock = (item) => {
  unlockItem.value = item;
  unlockDialog.value = true;
};

// Benutzer entsperren
const unlockUser = async () => {
  if (!unlockItem.value) return;

  try {
    const response = await axios.post(
      `${API_BASE_URL}admin/locked_users.php`,
      {
        action: "unlock",
        username: unlockItem.value.username,
        ip_address: unlockItem.value.ip_address,
      },
      {
        withCredentials: true,
      }
    );

    if (response.data.status === "success") {
      toast.success("Sperre erfolgreich aufgehoben!");
      await fetchLockedUsers();
    } else {
      toast.error(response.data.message || "Fehler beim Entsperren");
    }
  } catch (error) {
    toast.error("Fehler beim Entsperren des Benutzers");
  } finally {
    unlockDialog.value = false;
    unlockItem.value = null;
  }
};

// Login-Verlauf löschen
const clearLoginHistory = async (item) => {
  if (!confirm('Möchten Sie wirklich alle Login-Versuche für diesen Benutzer und diese IP-Adresse löschen?')) {
    return;
  }

  try {
    const response = await axios.delete(
      `${API_BASE_URL}loginAttempts.php`,
      {
        withCredentials: true,
        data: {
          action: "delete",
          username: item.username,
          ip_address: item.ip_address
        }
      }
    );

    if (response.data.status === "success") {
      toast.success(`${response.data.deleted_count} Einträge gelöscht`);
      await fetchLockedUsers();
      await fetchLoginAttempts();
    } else {
      toast.error(response.data.message || "Fehler beim Löschen");
    }
  } catch (error) {
    toast.error("Fehler beim Löschen des Login-Verlaufs");
  }
};

// Zeitformatierung für verbleibende Zeit
const formatRemainingTime = (minutes) => {
  if (minutes <= 0) return "Abgelaufen";
  if (minutes === 1) return "1 Minute";
  if (minutes < 60) return `${minutes} Minuten`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return hours === 1 ? "1 Stunde" : `${hours} Stunden`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

// History-Dialog öffnen
const showLoginHistory = async (item) => {
  historyItem.value = item;
  historyDialog.value = true;
  await fetchLoginHistory(item);
};

// Login-Verlauf laden
const fetchLoginHistory = async (item) => {
  loadingHistory.value = true;
  loginHistory.value = [];

  try {
    // Neue API verwenden für detailliertere Informationen
    const params = new URLSearchParams();
    params.append('username', item.username);
    params.append('ip_address', item.ip_address);
    params.append('days', 365); // Letztes Jahr
    params.append('limit', 100);
    params.append('action', 'get');

    const response = await axios.get(
      `${API_BASE_URL}loginAttempts.php?${params.toString()}`,
      {
        withCredentials: true,
      }
    );

    if (response.data.status === "success") {
      loginHistory.value = response.data.attempts;
    } else {
      toast.error(response.data.message || "Fehler beim Laden des Verlaufs");
    }
  } catch (error) {
    toast.error("Fehler beim Laden des Login-Verlaufs");
  } finally {
    loadingHistory.value = false;
  }
};

// Formatiere Datum und Zeit
const formatAttemptTime = (datetime) => {
  if (!datetime) return "-";

  const date = new Date(datetime);
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// ======== Announcements - Funktionen ========

// Announcement speichern
const saveAnnouncement = async () => {
  if (!announcementMessage.value.message) {
    toast.error("Bitte eine Nachricht eingeben!");
    return;
  }

  try {
    // 1. Nachricht in der Datenbank speichern
    const response = await axios.post(
      `${API_BASE_URL}announcement.php`,
      { ...announcementMessage.value, action: "save" },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Announcement gespeichert und veröffentlicht!");

      // 2. Nachricht über WebSocket versenden
      if (wsAnnouncement && wsAnnouncement.readyState === WebSocket.OPEN) {
        // ID hinzufügen, damit Clients sie identifizieren können
        const messageWithTimestamp = {
          type: announcementMessage.value.type, // Dies ist wichtig - "announcement" oder "warning"
          message: announcementMessage.value.message,
          id: Date.now().toString(),
          timestamp: Date.now(),
        };

        wsAnnouncement.send(JSON.stringify(messageWithTimestamp));
      }
    } else {
      toast.error(
        "Fehler beim Speichern: " +
          (response.data.message || "Unbekannter Fehler")
      );
    }
  } catch (error) {
    toast.error("Netzwerkfehler beim Senden des Announcements");
  }
};

// Announcement deaktivieren
const deactivateAnnouncement = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}announcement.php`,
      { deactivate: true, action: "deactivate" },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Aktives Announcement deaktiviert");

      // Optional: Eine "Deaktivierungs"-Nachricht senden
      if (wsAnnouncement && wsAnnouncement.readyState === WebSocket.OPEN) {
        wsAnnouncement.send(
          JSON.stringify({
            type: "announcement_cleared",
            timestamp: Date.now(),
          })
        );
      }
    } else {
      toast.error("Fehler: " + (response.data.message || "Unbekannter Fehler"));
    }
  } catch (error) {
    toast.error("Netzwerkfehler beim Deaktivieren");
  }
};

// ======== Initialisierung ========

onMounted(() => {

  // Direkt auf das admin-Flag prüfen, anstatt userIsAdmin zu verwenden
  const isUserAdmin =
    authStore.user?.admin === true ||
    authStore.user?.admin === 1 ||
    authStore.user?.admin === "1" ||
    authStore.user?.super_admin === true ||
    authStore.user?.super_admin === 1 ||
    authStore.user?.super_admin === "1" ||
    authStore.user?.developer === true ||
    authStore.user?.developer === 1 ||
    authStore.user?.developer === "1";

  if (!isUserAdmin) {
    toast.error("Keine Administratorrechte!");
    setTimeout(() => {
      window.location.href = "/";
    }, 2000);
    return;
  }

  fetchUsers();
  fetchCategories();
  fetchPreise();
  fetchTypen();
  fetchDurchwahlen();
  fetchKurztexte();
  fetchLockedUsers();
  fetchLoginAttempts(); // Login-Versuche laden
  initWebSocket();
});

// Add the toggle function
const toggleReturenSortingStatus = async (user) => {
  try {
    const newStatus = user.returen_sorting ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_returen_sorting",
        user_id: user.id,
        returen_sorting: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.returen_sorting = !user.returen_sorting; // Direkte Umkehrung des booleschen Wertes
      toast.success(
        `Benutzer ist jetzt ${user.returen_sorting ? "Retouren Sorting" : "kein Retouren Sorting"}!`
      );
    } else {
      toast.error(response.data.message || "Fehler beim Ändern des Status!");
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Status!");
  }
};

const toggleReturenSortingV2Status = async (user) => {
  try {
    const newStatus = user.returen_sorting_v2 ? 0 : 1;
    const response = await axios.post(
      `${API_BASE_URL}admin/users.php`,
      {
        action: "toggle_returen_sorting_v2",
        user_id: user.id,
        returen_sorting_v2: newStatus,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      user.returen_sorting_v2 = !user.returen_sorting_v2;
      toast.success(
        `Benutzer hat jetzt ${user.returen_sorting_v2 ? 'Zugriff auf' : 'keinen Zugriff auf'} Retouren Sorting V2!`
      );
    } else {
      toast.error(response.data.message || "Fehler beim Ändern des Status!");
    }
  } catch (error) {
    toast.error("Fehler beim Ändern des Status!");
  }
};
</script>

<style scoped>
/* ======== Grundlayout und Container ======== */
.admin-view {
  position: relative;
  width: 100%;
}

/* ======== Hintergrundelemente für Glasmorphismus ======== */
.background-elements {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
}

.ambient-light {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.15;
}

.ambient-light-1 {
  width: 600px;
  height: 600px;

  top: -200px;
  right: -100px;
  animation: pulse 15s infinite alternate;
}

.ambient-light-2 {
  width: 500px;
  height: 500px;

  bottom: -100px;
  left: -100px;
  animation: pulse 20s infinite alternate-reverse;
}

.ambient-light-3 {
  width: 400px;
  height: 400px;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 18s infinite alternate;
}

@keyframes pulse {
  0% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.15;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.1;
    transform: scale(1);
  }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

/* ======== Admin Tabs Container ======== */
.admin-tabs-container {
  position: relative;
  z-index: 10;
  margin-bottom: 20px;
}

.admin-tabs-wrapper {
  display: flex;
  background: rgba(30, 30, 40, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(76, 175, 80, 0.3) transparent;
  gap: 6px;
}

.admin-tabs-wrapper::-webkit-scrollbar {
  height: 6px;
}

.admin-tabs-wrapper::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.admin-tabs-wrapper::-webkit-scrollbar-thumb {
  background: rgba(76, 175, 80, 0.3);
  border-radius: 3px;
}

.admin-tabs-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(76, 175, 80, 0.5);
}

.admin-tab {
  position: relative;
  padding: 14px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.7);
  flex-shrink: 0;
}

.admin-tab:hover {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
}

.admin-tab.active {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.25) 0%, rgba(76, 175, 80, 0.15) 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
}

.tab-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.tab-title {
  font-size: 14px;
  font-weight: 500;
}

.tab-indicator {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background-color: #4caf50;
  border-radius: 2px;
}

/* ======== Haupt Admin Card ======== */
.admin-card {
  background: rgba(30, 30, 40, 0.7) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  position: relative;
  z-index: 10;
  margin-bottom: 30px;
}
.admin-divider {
  border-color: rgba(255, 255, 255, 0.08);
  margin: 0;
}

/* ======== Tab Content Container ======== */
.tab-content-container {
  padding: 24px; /* Erhöht von 20px auf 24px wie bei anderen Views */
}

/* ======== Section Header ======== */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-title {
  display: flex;
  align-items: center;
}

.header-title h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  -webkit-background-clip: text;
  background-clip: text;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  height: 38px;
  border-radius: 8px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
}

.refresh-btn {
  transition: transform 0.3s ease;
}

.refresh-btn:hover {
  transform: rotate(90deg);
}

.toggle-btn {
  height: 38px;
  border-radius: 8px;
  letter-spacing: 0.5px;
  text-transform: none;
}

/* ======== Filter Bar ======== */
.filter-bar {
  display: flex;
  margin-bottom: 20px;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.search-field {
  flex: 1;
  min-width: 240px;
  max-width: 400px;
}

.filter-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-chip {
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.filter-chip:hover {
  border-color: rgba(76, 175, 80, 0.6);
}

.selected-filter {
  background-color: rgba(76, 175, 80, 0.2) !important;
  border-color: rgba(76, 175, 80, 0.6) !important;
}

/* ======== User Cards Grid ======== */
.users-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

/* ======== Moderne Nutzerkarten ======== */
.user-card-modern {
  background: rgba(30, 30, 40, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.user-card-modern:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
  border-color: rgba(76, 175, 80, 0.3);
}

/* Header */
.user-card-modern-header {
  padding: 20px;
  background: rgba(25, 25, 35, 0.6);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.user-avatar-modern {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(76, 175, 80, 0.15);
  border: 2px solid rgba(76, 175, 80, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.avatar-admin {
  background: rgba(244, 67, 54, 0.15);
  border-color: rgba(244, 67, 54, 0.4);
}

.avatar-developer {
  background: rgba(255, 193, 7, 0.15);
  border-color: rgba(255, 193, 7, 0.4);
}

.user-name-block {
  flex: 1;
}

.user-name-modern {
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.2px;
}

.user-login-modern {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  font-weight: 400;
}

.status-chip {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  height: 26px;
  text-transform: uppercase;
}

/* Info Grid */
.user-info-grid {
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  word-break: break-word;
}

.info-value-small {
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
}

/* Rollen */
.user-roles-modern {
  padding: 0 20px 16px 20px;
}

.roles-label {
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

.roles-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.role-chip {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 28px;
}

.role-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  opacity: 1 !important;
}

/* Inaktive Rollen dezenter machen */
.role-chip.v-chip--variant-outlined {
  opacity: 0.6;
  border-color: rgba(255, 255, 255, 0.2);
}

.role-chip.v-chip--variant-outlined:hover {
  opacity: 1;
  border-color: rgba(255, 255, 255, 0.4);
}

/* Rolle hinzufügen */
.add-role-section {
  padding: 0 20px 16px 20px;
}

.add-role-btn {
  font-size: 12px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.3px;
  height: 32px;
}

.role-menu {
  background: rgba(30, 30, 40, 0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Actions */
.user-actions-modern {
  padding: 16px 20px;
  background: rgba(20, 20, 30, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.primary-actions {
  display: flex;
  gap: 8px;
}

/* ======== Edit Dialog Modernisierung ======== */
.edit-user-dialog {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.dialog-header-modern {
  padding: 24px 24px 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.dialog-title-block {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.dialog-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(139, 92, 246, 0.1));
  border-radius: 12px;
  border: 1px solid rgba(139, 92, 246, 0.3);
}

.dialog-title-text {
  flex: 1;
}

.dialog-main-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
  letter-spacing: -0.3px;
}

.dialog-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin: 4px 0 0 0;
  font-weight: 400;
}

.dialog-divider-modern {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Tabs */
.edit-tabs {
  padding: 0 24px;
  background: transparent;
}

.edit-tabs .v-tab {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 14px;
  min-height: 48px;
}

.edit-tabs .v-tab--selected {
  color: rgb(var(--v-theme-primary));
}

/* Tab Content */
.dialog-content-modern {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: rgba(20, 20, 30, 0.3);
}

/* Edit Section */
.edit-section {
  margin-bottom: 24px;
}

.edit-section:last-child {
  margin-bottom: 0;
}

.section-heading {
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  letter-spacing: -0.2px;
}

.section-divider {
  margin: 24px 0;
  border-color: rgba(255, 255, 255, 0.06) !important;
}

/* Modern Form Grid */
.modern-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.modern-input {
  border-radius: 10px !important;
}

.modern-input .v-field {
  border-radius: 10px;
  transition: all 0.2s ease;
}

.modern-input .v-field:hover {
  background: rgba(30, 30, 40, 0.6) !important;
}

.modern-input .v-field--focused {
  background: rgba(30, 30, 40, 0.7) !important;
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
}

/* Password Requirements Modern */
.password-requirements-modern {
  margin-top: 20px;
  padding: 16px;
  background: rgba(30, 30, 40, 0.4);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.requirements-heading {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.requirements-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  transition: all 0.2s ease;
  padding: 4px 0;
}

.requirement-item.met {
  color: rgba(76, 175, 80, 0.9);
}

.requirement-item span {
  flex: 1;
}

/* Dialog Actions */
.dialog-actions-modern {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  background: rgba(20, 20, 30, 0.4);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.modern-btn-text {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.2px;
  min-width: 90px;
  height: 40px;
}

.modern-btn-primary {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  min-width: 120px;
  height: 40px;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.modern-btn-primary:hover {
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.4);
  transform: translateY(-1px);
}

.modern-btn-success {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  min-width: 120px;
  height: 40px;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.modern-btn-success:hover {
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
  transform: translateY(-1px);
}

/* Register Dialog spezifische Styles */
.register-user-dialog .register-icon {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(76, 175, 80, 0.1));
  border-color: rgba(76, 175, 80, 0.3);
}

/* ======== Alte User Cards (Fallback) ======== */
.user-card {
  background: rgba(30, 30, 40, 0.5);
  border-radius: 12px;
  transition: all 0.3s ease;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.user-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
}

.user-card-header {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(40, 40, 50, 0.6);
}

.admin-header {
  background: linear-gradient(
    135deg,
    rgba(244, 67, 54, 0.2),
    rgba(40, 40, 50, 0.6)
  );
}

.developer-header {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.2),
    rgba(40, 40, 50, 0.6)
  );
}

.user-info {
  display: flex;
  align-items: center;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(76, 175, 80, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.admin-avatar {
  background: rgba(244, 67, 54, 0.2);
  border: 1px solid rgba(244, 67, 54, 0.3);
}

.user-name-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
}

.user-login {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}

.user-status {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.active-status {
  background: rgba(76, 175, 80, 0.15);
  color: #4caf50;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.inactive-status {
  background: rgba(244, 67, 54, 0.15);
  color: #f44336;
  border: 1px solid rgba(244, 67, 54, 0.3);
}

.user-card-content {
  padding: 16px 20px;
}

.user-detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}

.user-detail-item:last-child {
  margin-bottom: 16px;
}

.detail-text {
  margin-left: 10px;
}

.user-role-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.role-badge {
  font-size: 12px;
  font-weight: 500;
  text-transform: none;
}

.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.user-card-actions {
  padding: 12px 20px;
  background: rgba(30, 30, 40, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.action-button {
  flex: 1;
  min-width: 120px;
  font-size: 13px;
  text-transform: none;
  border-radius: 6px;
  padding: 0 16px;
  font-weight: 500;
}

/* ======== Modern Table ======== */
.modern-table-container {
  background: rgba(30, 30, 40, 0.3);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table th {
  background: rgba(40, 40, 50, 0.6);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  text-align: left;
  padding: 14px 20px;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modern-table td {
  padding: 12px 20px;
  color: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 14px;
}

.modern-table tr:last-child td {
  border-bottom: none;
}

.modern-table tr:hover td {
  background-color: rgba(255, 255, 255, 0.03);
}

.drag-column {
  width: 50px;
  text-align: center;
}

.drag-handle {
  cursor: move;
}

.actions-column {
  width: 100px;
  text-align: right;
}

.action-icon-btn {
  margin: 0 2px;
}

/* ======== Dialog Design ======== */
.modern-dialog {
  background: transparent;
}

.dialog-card {
  background: rgba(30, 30, 40, 0.9) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.card-header {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.title-container {
  display: flex;
  align-items: center;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  margin-left: 12px;
  color: rgba(255, 255, 255, 0.95);
}

.card-divider {
  border-color: rgba(255, 255, 255, 0.08);
  margin: 0;
}

.enhanced-card-content {
  padding: 20px;
}

.enhanced-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.form-field {
  width: 100%;
}

.enhanced-password-requirements {
  background: rgba(30, 30, 40, 0.7);
  border-radius: 10px;
  padding: 16px 20px;
  margin-top: 16px;
}

.requirements-title {
  font-size: 16px;
  font-weight: 500;
  margin-top: 0;
  margin-bottom: 12px;
  color: rgba(255, 255, 255, 0.9);
}

.requirements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.requirement {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  transition: all 0.2s ease;
}

.requirement.fulfilled {
  color: rgba(255, 255, 255, 0.9);
}

.requirement span {
  margin-left: 8px;
}

.enhanced-dialog-actions {
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(30, 30, 35, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.cancel-btn {
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
}

.submit-btn {
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
}

/* ======== Durchwahlen Karten ======== */
.durchwahlen-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.durchwahl-card {
  background: rgba(30, 30, 40, 0.5);
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.durchwahl-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  background: rgba(35, 35, 45, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}

.durchwahl-card-content {
  display: flex;
  flex: 1;
}

.durchwahl-number {
  font-size: 22px;
  font-weight: 600;
  color: #4caf50;
  margin-right: 16px;
  min-width: 50px;
  text-align: center;
}

.durchwahl-details {
  flex: 1;
}

.durchwahl-name {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 4px 0;
  color: rgba(255, 255, 255, 0.9);
}

.durchwahl-abteilung {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  display: flex;
  align-items: center;
}

.durchwahl-card-actions {
  display: flex;
  gap: 8px;
}

/* ======== Announcement Editor ======== */
.announcement-editor-card {
  background: rgba(30, 30, 40, 0.6) !important;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.announcement-editor-title {
  font-size: 18px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.announcement-preview-container {
  padding: 20px;
  background: rgba(25, 25, 30, 0.5);
  border-radius: 10px;
  display: flex;
  justify-content: center;
}

.announcement-preview-card {
  width: 100%;
  max-width: 600px;
  border-radius: 10px;
  overflow: hidden;
}

.announcement-preview-card.announcement {
  background: rgba(33, 150, 243, 0.15);
  border: 1px solid rgba(33, 150, 243, 0.3);
}

.announcement-preview-card.warning {
  background: rgba(244, 67, 54, 0.15);
  border: 1px solid rgba(244, 67, 54, 0.3);
}

.announcement-preview-content {
  padding: 16px;
  display: flex;
  gap: 16px;
}

.announcement-icon {
  margin-top: 2px;
}

.announcement-text-container {
  flex: 1;
}

.announcement-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.95);
}

.announcement-message {
  color: rgba(255, 255, 255, 0.85);
  white-space: pre-line;
}

.announcement-actions {
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
}

.dismiss-btn {
  color: rgba(255, 255, 255, 0.7);
}

.announcement-editor-actions {
  padding: 16px 20px;
  background: rgba(30, 30, 35, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

/* ======== Gesperrte Benutzer ======== */
.locked-users-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 20px;
  padding: 20px;
}

.locked-user-card {
  background: rgba(30, 30, 40, 0.6) !important;
  border-radius: 12px;
  border: 1px solid rgba(244, 67, 54, 0.2);
  transition: all 0.3s ease;
}

.locked-user-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(244, 67, 54, 0.15);
  border-color: rgba(244, 67, 54, 0.4);
}

.locked-user-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.locked-user-info {
  display: flex;
  align-items: flex-start;
  flex: 1;
}

.locked-user-name {
  font-size: 18px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 8px;
}

.locked-user-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.locked-user-status {
  display: flex;
  align-items: center;
}

.status-chip {
  font-weight: 600;
  padding: 8px 16px;
}

.locked-user-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(40, 40, 50, 0.4);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.stat-item:hover {
  background: rgba(40, 40, 50, 0.6);
}

.stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}

.stat-value {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  margin-left: auto;
}

.locked-user-actions {
  padding: 12px 16px;
  background: rgba(30, 30, 35, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.unlock-message {
  padding: 4px;
}

.unlock-details {
  margin-top: 16px;
  padding: 12px;
  background: rgba(40, 40, 50, 0.4);
  border-radius: 8px;
}

.success-header {
  background: linear-gradient(
    135deg,
    rgba(76, 175, 80, 0.1) 0%,
    rgba(76, 175, 80, 0.05) 100%
  );
}

/* ======== Login-Versuche ======== */
.login-attempts-header {
  padding: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: rgba(30, 30, 40, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.12);
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-details {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1;
}

.stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-card {
  background: rgba(30, 30, 40, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.login-attempts-table {
  background: rgba(30, 30, 40, 0.3) !important;
}

/* Detail Dialog */
.detail-section {
  margin-bottom: 20px;
}

.detail-section-title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
}

.detail-value {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
}

.user-agent-box {
  padding: 12px;
  background: rgba(20, 20, 30, 0.5);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  word-break: break-all;
  line-height: 1.5;
}

/* ======== Login-Verlauf Dialog ======== */
.history-header {
  margin-bottom: 20px;
  padding: 12px;
  background: rgba(40, 40, 50, 0.4);
  border-radius: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.history-table-container {
  margin-top: 16px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.history-table {
  background: rgba(30, 30, 40, 0.3) !important;
}

.history-table thead tr th {
  background: rgba(40, 40, 50, 0.6) !important;
  color: rgba(255, 255, 255, 0.9) !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.history-table tbody tr {
  transition: background-color 0.2s ease;
}

.history-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03) !important;
}

.history-table tbody tr td {
  padding: 10px 16px !important;
  color: rgba(255, 255, 255, 0.8) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
  font-size: 13px !important;
}

.history-table tbody tr.failed-attempt {
  background: rgba(244, 67, 54, 0.05) !important;
}

.history-table tbody tr.success-attempt {
  background: rgba(76, 175, 80, 0.05) !important;
}

.history-table tbody tr:last-child td {
  border-bottom: none !important;
}

/* Responsive Anpassungen */
@media (max-width: 960px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
    margin-top: 12px;
  }

  .users-grid {
    grid-template-columns: 1fr;
  }

  .enhanced-form-grid {
    grid-template-columns: 1fr;
  }

  .requirements-grid {
    grid-template-columns: 1fr;
  }

  .durchwahlen-grid {
    grid-template-columns: 1fr;
  }

  .locked-users-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .admin-tabs-wrapper {
    padding: 4px;
  }

  .admin-tab {
    padding: 10px 12px;
    min-width: auto;
  }

  .tab-title {
    font-size: 13px;
  }

  .header-title h1 {
    font-size: 20px;
  }

  .tab-content-container {
    padding: 16px 12px;
  }

  .enhanced-dialog-actions {
    padding: 12px 16px;
  }

  .user-card-actions {
    flex-direction: column;
  }

  .action-button {
    width: 100%;
  }
}

/* ======== Animations ======== */
.fade-transition-enter-active,
.fade-transition-leave-active {
  transition: opacity 0.3s ease;
}

.fade-transition-enter-from,
.fade-transition-leave-to {
  opacity: 0;
}

.admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}
</style>
